/**
 * Made-to-order doormat configurator.
 * Sizing, placement validation, canvas preview, and add-on cart pricing.
 */
import { CartAddEvent, ThemeEvents } from "@theme/events";
import { fetchConfig } from "@theme/utilities";

/**
 * @typedef {Object} DoormatVariant
 * @property {number|string} id
 * @property {string} [title]
 * @property {boolean} [available]
 * @property {number} [price]
 */

/**
 * @typedef {Object} PricingConfig
 * @property {boolean} enableAddonPricing
 * @property {number|string|null} addonVariantId
 * @property {number} addonUnitPrice
 * @property {boolean} [addonAvailable]
 * @property {string|null} [addonInventoryTracked]
 * @property {string|null} [addonInventoryPolicy]
 * @property {number|null} [addonInventoryQuantity]
 * @property {string|null} [addonProductTitle]
 * @property {boolean} [enableAppPricing]
 * @property {string} [appProxyUrl]
 */

/**
 * @typedef {Object} CartLineItem
 * @property {string} id
 * @property {number} quantity
 * @property {Record<string, string>} [properties]
 */

class DoormatConfigurator extends HTMLElement {
  /** @type {HTMLElement | null} */
  #productFormComponent = null;
  #isAddingToCart = false;
  #allowNativeFormSubmit = false;
  #addToCartSuccess = false;
  /** @type {((event: Event) => void) | null} */
  #variantUpdateHandler = null;
  /** @type {MutationObserver | null} */
  #soldOutUiObserver = null;
  /** @type {HTMLElement | null} */
  #soldOutUiSectionRoot = null;
  #isSuppressingSoldOutUi = false;
  /** @type {number | null} */
  #soldOutUiRaf = null;
  /** @type {number} */
  #addToCartLabel = "Add to cart";
  /** @type {number} */
  defaultDisplayPrice = 0;
  #activeSizeMode = "custom";
  /** @type {number} */
  maxShortSide = 180;
  // Fires once per page load, on the first meaningful interaction with the
  // configurator (size preset click, width/length typed, or placement
  // chosen) - see #trackConfiguratorStarted().
  #configuratorStartedTracked = false;

  // HC - loading overlay messages (read from data-loading-* block settings)
  /** @type {string} */ loadingProductTitle = "Custom doormat";
  /** @type {string} */ loadingMsgStart = "Custom-fitting your mat…";
  /** @type {string} */ loadingMsgCutting = "Cutting to {w} × {l} cm…";
  /** @type {string} */ loadingMsgDone = "Almost at your door.";
  /** @type {string} */ loadingHelper = "Made-to-measure takes about 10–15 seconds, worth it for a mat cut exactly to your doorstep.";
  /** @type {string} */ loadingPill = "Preparing your mat…";

  // HC - translatable labels (read from data-* theme settings)
  /** @type {string} */ priceFrom = "From";
  /** @type {string} */ errorMinMax = "Enter a value between {min} and {max} cm";
  /** @type {string} */ errorShortSide = "The size is too large. Only one side may be larger than {max} cm; the other side must be {max} cm or smaller.";
  /** @type {string} */ errorRecessed = "With recessed installation, the manufacturing size (+{added} cm on each side) would exceed the maximum. Please reduce your dimensions.";
  /** @type {string} */ labelPlacementRecessed = "In a recessed floor";
  /** @type {string} */ labelPlacementFloor = "On the floor";
  /** @type {string} */ labelWithMargin = "(with margin)";
  /** @type {string} */ labelTolerance = "(±1 cm)";
  /** @type {string} */ cartPropProduct = "Product";
  /** @type {string} */ cartPropWidth = "Horizontal (Width)";
  /** @type {string} */ cartPropLength = "Vertical (Length)";
  /** @type {string} */ cartPropPlacement = "Placement";
  /** @type {string} */ cartPropFinalSize = "Final size";

  /**
   * Background-prefetched variant data. When the user completes their
   * configuration (enters dimensions + placement) we immediately fire the
   * app-proxy resolve call so the variant exists by the time they click ATC.
   * @type {{ key: string, variantId: string, created: boolean } | null}
   */
  #prefetchedVariant = null;
  /** @type {Promise<{variantId:string,created:boolean}|null>|null} */
  #prefetchPromise = null;
  /** @type {string|null} */
  #prefetchKey = null;
  /** @type {Promise<string|null>|null} */
  #earlyPreviewUpload = null;
  /** @type {string|null} */
  #earlyPreviewConfigId = null;

  /** @type {string} */
  productId = "";
  /** @type {string} */
  productTitle = "";
  /** @type {string} */
  productHandle = "";
  /** @type {string} */
  productUrl = "";
  /** @type {string} */
  productImage = "";
  /** @type {number} */
  pricePerSqm = 100;
  /** @type {number} */
  setupFee = 0;
  /** @type {number} */
  minSize = 50; // Changed from 25 to 50cm
  /** @type {number} */
  maxSize = 350;
  /** @type {number} */
  patternScaleX = 10;
  /** @type {number} */
  patternScaleY = 10;
  centerPattern = false;
  /** @type {number} */
  margin = 2.5;
  /** @type {string | null} */
  patternUrl = null;
  /** @type {string} */
  moneyFormat = "{{amount}}";
  /** @type {string | null} */
  checkoutVariantId = null;
  /** @type {string} */
  defaultSizeKey = "single-door";
  /** @type {number | null} */
  currentWidth = null;
  /** @type {number | null} */
  currentLength = null;
  /** @type {string | null} */
  currentPlacement = null;
  /** @type {number | null} */
  lastVisualWidth = null;
  /** @type {number | null} */
  lastVisualLength = null;
  /** @type {HTMLImageElement | null} */
  patternImage = null;
  /** @type {Promise<HTMLImageElement | null> | null} */
  patternImageLoading = null;
  /** @type {DoormatVariant[]} */
  variants = [];
  /** @type {PricingConfig} */
  pricingConfig = {
    enableAddonPricing: false,
    addonVariantId: null,
    addonUnitPrice: 0,
  };
  /** @type {NodeListOf<HTMLElement>} */
  sizeButtons = /** @type {NodeListOf<HTMLElement>} */ (
    document.querySelectorAll(":empty")
  );
  /** @type {HTMLInputElement | null} */
  widthInput = null;
  /** @type {HTMLInputElement | null} */
  lengthInput = null;
  /** @type {HTMLElement | null} */
  widthError = null;
  /** @type {HTMLElement | null} */
  lengthError = null;
  /** @type {NodeListOf<HTMLElement>} */
  placementOptions = /** @type {NodeListOf<HTMLElement>} */ (
    document.querySelectorAll(":empty")
  );
  /** @type {HTMLElement | null} */
  placementError = null;
  /** @type {HTMLElement | null} */
  combinedError = null;
  /** @type {HTMLElement | null} */
  summarySection = null;
  /** @type {HTMLElement | null} */
  summaryPlacement = null;
  /** @type {HTMLElement | null} */
  summarySize = null;
  /** @type {HTMLElement | null} */
  summaryFinal = null;
  /** @type {HTMLElement | null} */
  priceDisplay = null;
  /** @type {HTMLElement | null} */
  viewerContainer = null;
  /** @type {HTMLCanvasElement | null} */
  viewerCanvas = null;
  /** @type {HTMLElement | null} */
  viewerPlaceholder = null;
  /** @type {HTMLElement | null} */
  globalError = null;
  /** @type {HTMLElement | null} */
  globalErrorText = null;
  /** @type {HTMLElement | null} */
  modal = null;
  /** @type {HTMLElement | null} */
  modalTitle = null;
  /** @type {HTMLElement | null} */
  modalContent = null;
  /** @type {HTMLElement | null} */
  modalImageWrap = null;
  /** @type {HTMLImageElement | null} */
  modalImage = null;
  /** @type {Record<string, { title?: string; content?: string; image?: string | null }>} */
  modalData = {};
  /** @type {() => void} */
  resizeHandler = () => {};
  /** @type {(event: KeyboardEvent) => void} */
  keydownHandler = () => {};

  connectedCallback() {
    this.productId = this.dataset.productId ?? "";
    this.productTitle = this.dataset.productTitle ?? "";
    this.productHandle = this.dataset.productHandle ?? "";
    this.productUrl = this.dataset.productUrl ?? "";
    this.productImage = this.dataset.productImage ?? "";
    this.pricePerSqm = parseFloat(this.dataset.pricePerSqm ?? "") || 100;
    this.setupFee = parseFloat(this.dataset.setupFee ?? "") || 0;
    this.minSize = parseInt(this.dataset.minSize ?? "", 10) || 25;
    this.maxSize = parseInt(this.dataset.maxSize ?? "", 10) || 350;
    this.maxShortSide = parseInt(this.dataset.maxShortSide ?? "", 10) || 180;
    this.loadingProductTitle = this.dataset.loadingProductTitle || "Custom doormat";
    this.loadingMsgStart     = this.dataset.loadingMsgStart     || "Custom-fitting your mat…";
    this.loadingMsgCutting   = this.dataset.loadingMsgCutting   || "Cutting to {w} × {l} cm…";
    this.loadingMsgDone      = this.dataset.loadingMsgDone      || "Almost at your door.";
    this.loadingHelper       = this.dataset.loadingHelper       || "Made-to-measure takes about 10–15 seconds, worth it for a mat cut exactly to your doorstep.";
    this.loadingPill         = this.dataset.loadingPill         || "Preparing your mat…";
    this.priceFrom            = this.dataset.priceFrom            || "From";
    this.errorMinMax          = this.dataset.errorMinMax          || "Enter a value between {min} and {max} cm";
    this.errorShortSide       = this.dataset.errorShortSide       || "The size is too large. Only one side may be larger than {max} cm; the other side must be {max} cm or smaller.";
    this.errorRecessed        = this.dataset.errorRecessed        || "With recessed installation, the manufacturing size (+{added} cm on each side) would exceed the maximum. Please reduce your dimensions.";
    this.labelPlacementRecessed = this.dataset.placementRecessed  || "In a recessed floor";
    this.labelPlacementFloor  = this.dataset.placementFloor       || "On the floor";
    this.labelWithMargin      = this.dataset.withMargin           || "(with margin)";
    this.labelTolerance       = this.dataset.tolerance            || "(±1 cm)";
    this.cartPropProduct      = this.dataset.cartProduct          || "Product";
    this.cartPropWidth        = this.dataset.cartWidth            || "Horizontal (Width)";
    this.cartPropLength       = this.dataset.cartLength           || "Vertical (Length)";
    this.cartPropPlacement    = this.dataset.cartPlacement        || "Placement";
    this.cartPropFinalSize    = this.dataset.cartFinalSize        || "Final size";
    this.patternScaleX = parseFloat(this.dataset.patternScaleX ?? "") || 10;
    this.patternScaleY = parseFloat(this.dataset.patternScaleY ?? "") || 10;
    this.centerPattern = this.dataset.centerPattern === "true";
    this.margin = parseFloat(this.dataset.margin ?? "") || 2.5;
    this.patternUrl = this.dataset.patternUrl ?? null;
    this.moneyFormat = this.dataset.moneyFormat ?? "{{amount}}";
    this.#addToCartLabel = this.dataset.addToCartLabel ?? "Add to cart";
    this.defaultDisplayPrice = parseFloat(this.dataset.defaultPrice ?? "") || 0;
    this.checkoutVariantId = this.dataset.checkoutVariantId ?? null;
    this.defaultSizeKey = this.dataset.defaultSizeKey ?? "single-door";

    this.currentWidth = null;
    this.currentLength = null;
    this.currentPlacement = null;
    this.lastVisualWidth = null;
    this.lastVisualLength = null;
    this.patternImage = null;
    this.patternImageLoading = null;

    this.variants = this.#readVariants();
    this.pricingConfig = this.#readPricingConfig();

    this.sizeButtons = this.querySelectorAll("[data-size]");
    this.widthInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector("[data-width-input]")
    );
    this.lengthInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector("[data-length-input]")
    );
    this.widthError = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-width-error]")
    );
    this.lengthError = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-length-error]")
    );
    this.placementOptions = this.querySelectorAll("[data-placement]");
    this.placementError = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-placement-error]")
    );
    this.combinedError = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-combined-error]")
    );
    this.summarySection = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-summary]")
    );
    this.summaryPlacement = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-summary-placement]")
    );
    this.summarySize = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-summary-size]")
    );
    this.summaryFinal = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-summary-final]")
    );
    this.priceDisplay = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-price]")
    );
    this.viewerContainer = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-viewer-container]")
    );
    this.viewerCanvas = /** @type {HTMLCanvasElement | null} */ (
      this.querySelector("[data-viewer-canvas]")
    );
    this.viewerPlaceholder = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-viewer-placeholder]")
    );
    this.globalError = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-global-error]")
    );
    this.globalErrorText = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-global-error-text]")
    );

    this.modal = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-modal]")
    );
    this.modalTitle = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-modal-title]")
    );
    this.modalContent = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-modal-content]")
    );
    this.modalImageWrap = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-modal-image-wrap]")
    );
    this.modalImage = /** @type {HTMLImageElement | null} */ (
      this.querySelector("[data-modal-image]")
    );
    this.modalData = this.#readModalData();

    this.resizeHandler = () => this.updateViewer();
    this.keydownHandler = (event) => {
      if (event.key === "Escape" && this.modal?.classList.contains("visible")) {
        this.closeModal();
      }
    };

    this.#bindEvents();
    this.#setupCartIntegration();
    this.#setupSoldOutUiSuppression();
    this.#preloadPattern();
    this.#initializeDefaultState();

    window.addEventListener("resize", this.resizeHandler);
    document.addEventListener("keydown", this.keydownHandler);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.resizeHandler);
    document.removeEventListener("keydown", this.keydownHandler);
    if (this.#variantUpdateHandler) {
      document.removeEventListener(
        ThemeEvents.variantUpdate,
        this.#variantUpdateHandler,
      );
    }
    this.#soldOutUiObserver?.disconnect();
    if (this.#soldOutUiRaf !== null) {
      cancelAnimationFrame(this.#soldOutUiRaf);
      this.#soldOutUiRaf = null;
    }
  }

  #readVariants() {
    const node = this.querySelector("[data-variants-json]");
    if (!node?.textContent) return [];
    try {
      const data = JSON.parse(node.textContent);
      if (data.checkoutVariantId && !this.checkoutVariantId) {
        this.checkoutVariantId = String(data.checkoutVariantId);
      }
      return Array.isArray(data.variants)
        ? /** @type {DoormatVariant[]} */ (data.variants)
        : [];
    } catch {
      return [];
    }
  }

  #readPricingConfig() {
    const node = this.querySelector("[data-pricing-json]");
    if (!node?.textContent) {
      return {
        enableAddonPricing: false,
        addonVariantId: null,
        addonUnitPrice: 0,
      };
    }
    try {
      return /** @type {PricingConfig} */ (JSON.parse(node.textContent));
    } catch {
      return {
        enableAddonPricing: false,
        addonVariantId: null,
        addonUnitPrice: 0,
      };
    }
  }

  #readModalData() {
    const node = this.querySelector("[data-modal-json]");
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch {
      return {};
    }
  }

  #bindEvents() {
    this.sizeButtons.forEach((button) => {
      button.addEventListener("click", () =>
        this.handleSizeButtonClick(button),
      );
    });

    this.widthInput?.addEventListener("input", () => {
      this.#trackConfiguratorStarted();
      this.handleInputChange(true);
    });
    this.lengthInput?.addEventListener("input", () => {
      this.#trackConfiguratorStarted();
      this.handleInputChange(true);
    });

    this.placementOptions.forEach((option) => {
      option.addEventListener("click", (event) => {
        const target = /** @type {HTMLElement | null} */ (
          event.target instanceof Element ? event.target : null
        );
        if (target?.closest("[data-modal-open]")) return;
        this.handlePlacementClick(option);
      });
    });

    this.querySelectorAll("[data-tooltip]").forEach((wrap) => {
      const trigger = wrap.querySelector("button");
      trigger?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const isOpen = wrap.classList.contains("is-open");
        this.querySelectorAll("[data-tooltip]").forEach((item) => {
          item.classList.remove("is-open");
          item.querySelector("button")?.setAttribute("aria-expanded", "false");
        });
        if (!isOpen) {
          wrap.classList.add("is-open");
          trigger.setAttribute("aria-expanded", "true");
        } else {
          // Blur so :focus-within doesn't keep the tooltip visible after closing
          trigger.blur();
        }
      });
    });

    document.addEventListener("click", (event) => {
      const target = event.target instanceof Node ? event.target : null;
      if (!target || !this.contains(target)) {
        this.querySelectorAll("[data-tooltip]").forEach((item) => {
          item.classList.remove("is-open");
          item.querySelector("button")?.setAttribute("aria-expanded", "false");
        });
      }
    });

    this.querySelectorAll("[data-modal-open]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const modalButton = /** @type {HTMLElement} */ (button);
        this.openModal(modalButton.dataset.modalOpen);
      });
    });

    this.querySelectorAll("[data-modal-close]").forEach((button) => {
      button.addEventListener("click", () => this.closeModal());
    });
  }

  #setupCartIntegration() {
    this.#productFormComponent = document.querySelector(
      `product-form-component[data-product-id="${this.productId}"]`,
    );

    // Set madeToOrder IMMEDIATELY so product-form.js's handleSubmit always
    // calls event.preventDefault() and returns early for this product - even
    // before the form submit listener is attached below. Previously this was
    // delayed to the moment the listener was attached (to avoid a race where
    // handleSubmit skipped preventDefault and let the form submit natively
    // before the doormat listener existed). That race no longer applies:
    // handleSubmit now calls event.preventDefault() when it returns early,
    // so a click in the brief window before the listener attaches just does
    // nothing (one missed click) rather than adding the wrong variant.
    // Setting it early also prevents the real bug: when madeToOrder was NOT
    // yet set, the document-level capture listener fired handleSubmit FIRST
    // (before our form-level capture), handleSubmit saw madeToOrder=false and
    // initiated its own AJAX add for the default variant - then our listener
    // fired and ALSO added the custom variant, producing two cart lines.
    if (this.#productFormComponent) {
      this.#productFormComponent.dataset.madeToOrder = "true";
    }

    this.#variantUpdateHandler = (event) => {
      const variantEvent =
        /** @type {CustomEvent<{ data?: { productId?: string } }>} */ (event);
      if (variantEvent.detail?.data?.productId !== this.productId) return;
      this.#syncCheckoutVariantToForm();
      this.#suppressSoldOutUi();
    };
    document.addEventListener(
      ThemeEvents.variantUpdate,
      this.#variantUpdateHandler,
    );

    const form = this.#productFormComponent?.querySelector(
      'form[data-type="add-to-cart-form"]',
    );
    if (!form || !this.#productFormComponent) {
      window.setTimeout(() => this.#setupCartIntegration(), 250);
      return;
    }

    if (form.dataset.doormatCartBound === "true") return;
    form.dataset.doormatCartBound = "true";
    // madeToOrder is already set above the form-existence check.

    form.addEventListener(
      "submit",
      (event) => {
        // CRITICAL: Stop ALL propagation immediately.
        // The document-level capture listener in component.js has already
        // called product-form-component.handleSubmit, which now calls
        // event.preventDefault() and returns early for madeToOrder products,
        // so no AJAX add-to-cart is initiated from that path. Our listener
        // (form-level capture, fires after document-level capture) takes
        // sole ownership of the add-to-cart logic.
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();

        if (this.#isAddingToCart) {
          return;
        }

        // Clear any stale suppression then set it fresh before validation.
        delete document.body.dataset.suppressCartDrawer;
        document.body.dataset.suppressCartDrawer = "true";

        const errors = this.#validate();
        if (errors.length > 0) {
          // Validation failed - clear suppression so the cart drawer remains
          // accessible (e.g. the shopper can open it manually).
          delete document.body.dataset.suppressCartDrawer;
          this.showGlobalError(errors.join(" · "));
          this.#scrollIntoView();
          return;
        }

        if (this.#shouldUseAppPricing()) {
          this.#addToCartViaApp();
          return;
        }

        if (this.pricingConfig.enableAddonPricing) {
          this.#addToCartWithAddons();
          return;
        }

        // Standard path: no dynamic pricing. Add the resolved variant with
        // custom properties directly via AJAX (no requestSubmit() delegation
        // to product-form-component, which now blocks for madeToOrder).
        delete document.body.dataset.suppressCartDrawer;
        this.#addToCartStandard();
      },
      { capture: true, passive: false },
    );

    this.#suppressSoldOutUi();
    this.#syncCheckoutVariantToForm();
    this.#patchStickyAtcForValidation();
  }

  /**
   * Patches the sticky add-to-cart component's handleAddToCartClick so it
   * validates the doormat configuration BEFORE the button animation and
   * fly-to-cart start. Without this, the sticky ATC triggers its "Added"
   * animation immediately (before the form submit runs), making it look like
   * the product was added even when there are validation errors.
   */
  #patchStickyAtcForValidation() {
    const stickyAtc = /** @type {any} */ (
      document.querySelector(
        `sticky-add-to-cart[data-product-id="${this.productId}"]`,
      )
    );
    if (!stickyAtc || typeof stickyAtc.handleAddToCartClick !== "function") return;

    const original = stickyAtc.handleAddToCartClick;

    stickyAtc.handleAddToCartClick = async () => {
      // Validate before allowing the animation or click to proceed.
      this.hideGlobalError();
      const msgs = [];
      if (!this.currentWidth || !this.currentLength) {
        msgs.push("Please enter the dimensions");
      }
      if (!this.currentPlacement) {
        msgs.push("Please select a placement option");
        this.placementOptions.forEach((opt) => opt.classList.add("error"));
        if (this.placementError) {
          this.placementError.textContent = "Please select how the mat will be placed";
        }
      }
      if (msgs.length > 0) {
        this.showGlobalError(msgs.join(" · "));
        this.#scrollIntoView();
        return;
      }
      // Validation passed - proceed with normal sticky ATC behaviour.
      return original();
    };
  }

  #setupSoldOutUiSuppression() {
    this.#suppressSoldOutUi();

    this.#soldOutUiSectionRoot =
      this.#productFormComponent?.closest(".shopify-section") ||
      document
        .querySelector(`variant-picker[data-product-id="${this.productId}"]`)
        ?.closest(".shopify-section") ||
      null;

    if (!this.#soldOutUiSectionRoot) return;

    this.#soldOutUiObserver = new MutationObserver(() => {
      this.#scheduleSoldOutUiSuppression();
    });
    this.#soldOutUiObserver.observe(this.#soldOutUiSectionRoot, {
      childList: true,
      subtree: true,
    });
  }

  #scheduleSoldOutUiSuppression() {
    if (this.#soldOutUiRaf !== null) return;

    this.#soldOutUiRaf = requestAnimationFrame(() => {
      this.#soldOutUiRaf = null;
      this.#suppressSoldOutUi();
    });
  }

  #suppressSoldOutUi() {
    if (this.#isSuppressingSoldOutUi) return;
    this.#isSuppressingSoldOutUi = true;
    this.#soldOutUiObserver?.disconnect();

    try {
      this.#enableAddToCartButtons();
      this.#fixAddToCartButtonLabels();

      document
        .querySelectorAll(
          `product-form-component[data-product-id="${this.productId}"] [ref="addToCartButton"], sticky-add-to-cart[data-product-id="${this.productId}"] [ref="addToCartButton"]`,
        )
        .forEach((button) => {
          if (!(button instanceof HTMLButtonElement)) return;
          if (button.disabled) button.disabled = false;
          if (button.hasAttribute("aria-disabled"))
            button.removeAttribute("aria-disabled");
          // Prevent AddToCartComponent.handleClick from triggering the
          // "added" animation on every click - the animation should only
          // fire after a confirmed successful add (via #triggerAddedState),
          // not when the click fails validation or while the async add is
          // in progress. Setting data-puppet="true" disables handleClick's
          // own animateAddToCart call without affecting #triggerAddedState.
          button.dataset.puppet = "true";
        });

      this.#soldOutUiSectionRoot
        ?.querySelectorAll("product-inventory")
        .forEach((inventory) => {
          if (!inventory.hasAttribute("hidden"))
            inventory.setAttribute("hidden", "");
        });

      document
        .querySelectorAll(
          `product-form-component[data-product-id="${this.productId}"] [ref="addToCartTextError"]`,
        )
        .forEach((errorNode) => {
          errorNode.classList.add("hidden");
          if (!errorNode.hasAttribute("hidden"))
            errorNode.setAttribute("hidden", "");
        });

      this.#soldOutUiSectionRoot
        ?.querySelectorAll(".product-form-text__error")
        .forEach((errorNode) => {
          errorNode.classList.add("hidden");
          if (!errorNode.hasAttribute("hidden"))
            errorNode.setAttribute("hidden", "");
        });

      const picker = document.querySelector(
        `variant-picker[data-product-id="${this.productId}"]`,
      );
      picker
        ?.querySelectorAll('input[type="radio"][aria-disabled="true"]')
        .forEach((input) => {
          if (input instanceof HTMLInputElement) {
            input.removeAttribute("aria-disabled");
          }
        });
    } finally {
      this.#isSuppressingSoldOutUi = false;

      if (this.#soldOutUiSectionRoot && this.#soldOutUiObserver) {
        this.#soldOutUiObserver.observe(this.#soldOutUiSectionRoot, {
          childList: true,
          subtree: true,
        });
      }
    }
  }

  #syncCheckoutVariantToForm() {
    const variantId = this.#resolveVariantId();
    const input = /** @type {HTMLInputElement | null} */ (
      this.#productFormComponent?.querySelector('[name="id"]')
    );
    if (input && variantId) {
      const nextVariantId = String(variantId);
      const variantChanged = input.value !== nextVariantId;
      input.value = nextVariantId;
      if (variantChanged) {
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  }

  /**
   * @param {{ variantId?: string | null, variantTitle?: string | null }} selection
   */
  #applyVariantSelection(selection) {
    this.#syncVariantPicker(selection);
    this.#syncCheckoutVariantToForm();
    this.#suppressSoldOutUi();
  }

  /**
   * @param {{ variantId?: string | null, variantTitle?: string | null }} selection
   */
  #syncVariantPicker(selection) {
    const { variantId, variantTitle } = selection;
    const picker = document.querySelector(
      `variant-picker[data-product-id="${this.productId}"]`,
    );

    if (!picker) {
      if (variantId) this.#updateVariantUrl(variantId);
      return;
    }

    /** @type {HTMLInputElement | null} */
    let matchedInput = null;

    if (variantId) {
      const byId = picker.querySelector(
        `input[data-variant-id="${variantId}"]`,
      );
      matchedInput = byId instanceof HTMLInputElement ? byId : null;
    }

    if (!matchedInput && variantTitle) {
      const target = variantTitle.trim().toLowerCase();
      const targetSuffix = target.split(" - ").pop()?.trim() || target;

      picker.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (!(input instanceof HTMLInputElement) || matchedInput) return;

        const value = input.value.trim().toLowerCase();
        const ariaLabel = (
          (input.getAttribute("aria-label") || "").split(" - ")[0] ?? ""
        )
          .trim()
          .toLowerCase();

        if (
          value === target ||
          ariaLabel === target ||
          value === targetSuffix ||
          ariaLabel === targetSuffix ||
          target.endsWith(` - ${value}`) ||
          target.endsWith(value)
        ) {
          matchedInput = input;
        }
      });
    }

    if (!matchedInput) {
      if (variantId) this.#updateVariantUrl(variantId);
      return;
    }

    if (!matchedInput.checked) {
      matchedInput.checked = true;
      matchedInput.dispatchEvent(new Event("change", { bubbles: true }));
      return;
    }

    if (variantId) this.#updateVariantUrl(variantId);
  }

  /** @param {string|number} variantId */
  #updateVariantUrl(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set("variant", String(variantId));

    if (url.href !== window.location.href) {
      history.replaceState({}, "", url.toString());
    }
  }

  #fixAddToCartButtonLabels() {
    document
      .querySelectorAll(
        `product-form-component[data-product-id="${this.productId}"] [ref="addToCartButton"] .add-to-cart-text__content span span`,
      )
      .forEach((label) => {
        const text = label.textContent?.trim().toLowerCase() || "";
        if (
          text.includes("sold out") ||
          text.includes("unavailable") ||
          text.includes("uitverkocht") ||
          text.includes("ausverkauft") ||
          text.includes("vergriffen") ||
          text.includes("épuisé") ||
          text.includes("indisponible")
        ) {
          label.textContent = this.#addToCartLabel;
        }
      });
  }

  #shouldUseAppPricing() {
    return (
      Boolean(this.pricingConfig.enableAppPricing) &&
      Boolean(this.currentWidth) &&
      Boolean(this.currentLength)
    );
  }

  #isThemeEditorPreview() {
    return (
      Boolean(window.Shopify?.designMode) ||
      window.location.search.includes("oseid=")
    );
  }

  /**
   * Calls the app proxy to resolve (or create) the price variant for the
   * current configuration. Shared by both the prefetch path and the live
   * ATC path so the logic is never duplicated.
   * @param {number} price euros
   * @returns {Promise<{variantId:string,created:boolean}>}
   */
  async #resolveVariantFromProxy(price) {
    // For recessed installation, use manufacturing dimensions (customer dims + margin)
    // so the server creates/looks up a variant at the correct (larger) price.
    const w = this.currentPlacement === "recessed"
      ? (this.currentWidth ?? 0) + this.margin * 2
      : this.currentWidth;
    const h = this.currentPlacement === "recessed"
      ? (this.currentLength ?? 0) + this.margin * 2
      : this.currentLength;

    const resolveRes = await this.#postAppProxy("", {
      productId: this.productId,
      width: w,
      height: h,
      price,
      // setupFee lets the server include it in its own price calculation if needed.
      setupFee: this.setupFee > 0 ? this.setupFee : undefined,
      productImage: this.productImage || undefined,
    });

    const responseText = await resolveRes.text();
    let data;
    try {
      data = JSON.parse(responseText);
    } catch {
      const preview = responseText.replace(/\s+/g, " ").trim().slice(0, 160);
      throw new Error(
        resolveRes.ok
          ? "Configurator app returned an invalid response."
          : preview
            ? `Configurator app error (${resolveRes.status}): ${preview}`
            : `Configurator app error (${resolveRes.status}). The app may be waking up — wait 30s and try again.`,
      );
    }

    if (!resolveRes.ok) {
      throw new Error(
        data.error || "Could not prepare checkout for this configuration.",
      );
    }

    return data;
  }

  /**
   * Starts a background variant-resolution call as soon as the user's
   * configuration is complete (width + length + placement all set). If the
   * user clicks ATC before the call finishes, addToCartViaApp() will await
   * the in-flight promise instead of starting a duplicate request, reducing
   * the perceived latency from 1–5 s to near zero on the happy path.
   */
  #maybePrefetchVariant() {
    if (!this.#shouldUseAppPricing()) return;
    if (!this.currentWidth || !this.currentLength || !this.currentPlacement) return;
    if (this.#isThemeEditorPreview()) return;

    const price = this.#getTargetPriceCents() / 100;
    const key = `${this.productId}:${this.currentWidth}:${this.currentLength}:${price}`;

    // Already have a result or an in-flight call for this exact config.
    if (this.#prefetchKey === key) return;

    this.#prefetchedVariant = null;
    this.#prefetchKey = key;

    console.log("[doormat-configurator] Prefetching variant for", key);

    this.#prefetchPromise = this.#resolveVariantFromProxy(price)
      .then((data) => {
        if (this.#prefetchKey === key) {
          this.#prefetchedVariant = { key, variantId: data.variantId, created: Boolean(data.created), propagated: Boolean(data.propagated), resolvedAt: Date.now() };
          console.log("[doormat-configurator] Prefetch complete", data.variantId, data.created ? "(new)" : "(existing)", data.propagated ? "[propagated ✓]" : "[not propagated]");

          // New variant: start preview upload immediately so it races the
          // propagation wait and is likely done before the user clicks ATC.
          if (data.created) {
            const dataUrl = this.#capturePreviewDataUrl();
            if (dataUrl) {
              this.#earlyPreviewConfigId = `${Date.now().toString(36)}-pre`;
              this.#earlyPreviewUpload = this.#uploadPreviewImage(dataUrl, this.#earlyPreviewConfigId, data.variantId);
              console.log("[doormat-configurator] Early preview upload started");
            }
          }
        }
        return { ...data, resolvedAt: Date.now() };
      })
      .catch((err) => {
        console.warn("[doormat-configurator] Prefetch failed (will retry on ATC)", err);
        if (this.#prefetchKey === key) {
          this.#prefetchKey = null;
          this.#prefetchPromise = null;
        }
        return null;
      });
  }

  /**
   * Call the app proxy. Uses GET with query params (most reliable through Shopify proxy).
   * @param {string} pathSuffix
   * @param {Record<string, unknown>} payload
   */
  async #postAppProxy(pathSuffix = "", payload = {}, usePost = false) {
    const baseUrl = (
      this.pricingConfig.appProxyUrl || "/apps/configurator"
    ).replace(/\/$/, "");

    if (usePost) {
      // Use POST for large payloads (like images)
      const url = `${baseUrl}${pathSuffix}`;
      return fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify(payload),
      });
    }

    // Use GET for small payloads
    const params = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.set(key, String(value));
      }
    });

    const query = params.toString();
    const url = query
      ? `${baseUrl}${pathSuffix}?${query}`
      : `${baseUrl}${pathSuffix}`;

    return fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  }

  async #addToCartViaApp() {
    const errors = this.#validate();
    if (errors.length > 0) {
      this.showGlobalError(errors.join(" · "));
      this.#scrollIntoView();
      return;
    }

    if (!this.currentWidth || !this.currentLength) {
      this.showGlobalError("Please enter the dimensions.");
      this.#scrollIntoView();
      return;
    }

    if (this.#isThemeEditorPreview()) {
      this.showGlobalError(
        "App pricing cannot be tested in the theme editor. Open the live product page in a new tab to test add to cart.",
      );
      this.#scrollIntoView();
      return;
    }

    // CRITICAL: Disable ALL ATC buttons immediately and set loading state
    this.#isAddingToCart = true;
    this.#setAddToCartLoading(true);
    this.#disableAllATCButtons();
    this.hideGlobalError();

    // Open the drawer right away (in a loading state) instead of leaving the
    // shopper staring at the page for however long the preview upload +
    // variant creation + propagation retries take - see #openCartDrawerLoading.
    this.#openCartDrawerLoading();

    // Note: suppressCartDrawer is already set by form submit handler

    try {
      const price = this.#getTargetPriceCents() / 100;
      const prefetchKey = `${this.productId}:${this.currentWidth}:${this.currentLength}:${price}`;

      let resolveData;

      if (this.#prefetchedVariant?.key === prefetchKey) {
        // Variant was already resolved in the background — zero wait.
        console.log("[doormat-configurator] Using prefetched variant", this.#prefetchedVariant.variantId);
        resolveData = this.#prefetchedVariant;
      } else if (this.#prefetchKey === prefetchKey && this.#prefetchPromise) {
        // Prefetch is still in-flight — wait for it instead of starting a
        // duplicate request (saves 1-5 seconds vs. a cold call).
        console.log("[doormat-configurator] Waiting for in-flight prefetch…");
        resolveData = await this.#prefetchPromise;
        if (!resolveData) {
          // Prefetch failed — fall through to fresh call below.
          resolveData = await this.#resolveVariantFromProxy(price);
        }
      } else {
        // No prefetch available — call the proxy now.
        resolveData = await this.#resolveVariantFromProxy(price);
      }

      // Invalidate the prefetch cache so the next ATC triggers a fresh call.
      this.#prefetchedVariant = null;
      this.#prefetchKey = null;
      this.#prefetchPromise = null;

      const configurationId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      const properties = this.#buildLineItemProperties(configurationId);
      properties["_Calculated price"] = this.formatMoney(price);
      properties["_Pricing mode"] = "app";
      properties["_configuration_type"] = "app";

      // Use the product image immediately — no blocking on canvas preview.
      // Preview is uploaded in the background after cart add succeeds.
      if (this.productImage) {
        properties._product_image = this.productImage;
      } else {
        const productImageElement =
          document.querySelector(`img[src*="products/${this.productHandle}"]`) ||
          document.querySelector(".product__media img");
        if (productImageElement instanceof HTMLImageElement && productImageElement.src) {
          properties._product_image = productImageElement.src;
        }
      }
      properties._product_title = this.productTitle;
      properties._product_handle = this.productHandle;
      properties._product_url = this.productUrl;
      properties._configured_product_id = this.productId;

      await this.#attachPreviewToProperties(properties, configurationId, resolveData.variantId);

      const variantId = String(resolveData.variantId).replace(
        /^gid:\/\/shopify\/ProductVariant\//,
        "",
      );

      const item = {
        id: variantId,
        quantity: 1,
        properties,
      };

      // If the server confirmed propagation, cart/add.js should succeed on the
      // first attempt. If not, add a local wait to give Shopify time to catch up.
      if (resolveData.created) {
        console.log("[doormat-configurator] Propagation status:", resolveData.propagated ? "confirmed by server ✓ — skipping wait" : "NOT confirmed — waiting + retries");
      }
      if (resolveData.created && !resolveData.propagated) {
        const PROPAGATION_MS = 2800;
        const elapsed = Date.now() - (resolveData.resolvedAt ?? Date.now());
        const waitMs = Math.max(0, PROPAGATION_MS - elapsed);
        if (waitMs > 0) await this.#delay(waitMs);
      }

      // Always use aggressive retries for new variants — even when the server confirmed
      // propagation, the cart API may still lag slightly behind the storefront check.
      let finalCart;
      try {
        finalCart = await this.#addDoormatToCart(item, resolveData.created);
      } catch (addErr) {
        // Existing variant is sold out (inventory exhausted) — ask the proxy
        // for a fresh variant and try once more.
        if (addErr instanceof Error && addErr.message === "__SOLD_OUT_EXISTING__") {
          console.log("[doormat-configurator] Existing variant sold out, re-resolving fresh variant from proxy...");
          const freshData = await this.#resolveVariantFromProxy(price);
          const freshVariantId = String(freshData.variantId).replace(
            /^gid:\/\/shopify\/ProductVariant\//,
            "",
          );
          if (freshData.created && !freshData.propagated) {
            const PROPAGATION_MS = 2800;
            const elapsed = Date.now() - (freshData.resolvedAt ?? Date.now());
            const waitMs = Math.max(0, PROPAGATION_MS - elapsed);
            if (waitMs > 0) await this.#delay(waitMs);
          }
          const freshItem = { ...item, id: freshVariantId };
          // preventRetry=true so a second sold-out gives a clear message instead of looping.
          finalCart = await this.#addDoormatToCart(freshItem, freshData.created, true);
          // Use fresh resolve data for the CartAddEvent below.
          resolveData = { ...freshData, resolvedAt: Date.now() };
        } else {
          throw addErr;
        }
      }
      this.#triggerAddedState();

      // Mark as successful so we can open the drawer in finally block
      this.#addToCartSuccess = true;
      console.log("[doormat-configurator] Add to cart succeeded, marked success flag");

      // IMPORTANT: clear suppression BEFORE dispatching CartAddEvent, not after
      // (that used to only happen in the `finally` block, below). Other theme
      // components (e.g. component-cart-items.js's #handleCartUpdate, and
      // product-form.js's own CartAddEvent listener) check this same flag and
      // silently skip re-rendering the cart drawer's contents while it's set -
      // dispatching the event with suppression still active meant the drawer
      // opened later showing stale contents instead of the item we just added.
      delete document.body.dataset.suppressCartDrawer;
      this.#setCartDrawerLoadingOverlay(false);

      this.#productFormComponent?.dispatchEvent(
        new CartAddEvent(finalCart, String(resolveData.variantId), {
          source: "product-form-component",
          itemCount: 1,
          productId: this.productId,
          sections: finalCart?.sections,
        }),
      );

      // Trigger cart refresh immediately so data is ready when drawer opens
      document.dispatchEvent(new CustomEvent('theme:cart-refresh'));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Something went wrong while adding to cart. Please try again.";
      console.error(
        "[doormat-configurator] app pricing add to cart failed",
        error,
      );
      this.showGlobalError(message);
      this.#scrollIntoView();
      this.#addToCartSuccess = false;
      this.#setCartDrawerLoadingOverlay(false);
      // Nothing actually made it into the cart - close the drawer we opened
      // early rather than leaving it open with stale/loading content while
      // the real error message shows on the page instead.
      const cartDrawer = /** @type {{ closeDialog?: () => void }} */ (
        document.querySelector("cart-drawer-component")
      );
      cartDrawer?.closeDialog?.();
    } finally {
      this.#isAddingToCart = false;
      this.#setAddToCartLoading(false);
      // Re-enable cart drawer
      delete document.body.dataset.suppressCartDrawer;
      console.log("[doormat-configurator] Removed suppression flag, success flag:", this.#addToCartSuccess);

      // Only open the cart drawer if add was successful
      if (this.#addToCartSuccess) {
        // Wait a moment for cart data to propagate, then open drawer
        setTimeout(() => {
          const cartDrawer = document.querySelector("cart-drawer-component");
          if (cartDrawer && typeof cartDrawer.open === "function") {
            console.log("[doormat-configurator] Opening cart drawer after successful add");
            cartDrawer.open();
          } else {
            console.warn("[doormat-configurator] Cart drawer not found or has no open method", { cartDrawer });
          }
        }, 150); // Wait for cart data to settle before opening drawer
      }
      this.#addToCartSuccess = false; // Reset flag
    }
  }

  async #addToCartWithAddons() {
    const pricingError = this.#validatePricingConfig();
    if (pricingError) {
      this.showGlobalError(pricingError);
      this.#scrollIntoView();
      return;
    }

    const cartPlan = await this.#buildCartPlan();
    if (!cartPlan) return;

    this.#isAddingToCart = true;
    this.#setAddToCartLoading(true);
    this.hideGlobalError();

    // Open the drawer right away (in a loading state) - see the equivalent
    // comment in #addToCartViaApp.
    this.#openCartDrawerLoading();

    // Note: suppressCartDrawer is already set by form submit handler

    try {
      if (cartPlan.skipBaseItem) {
        const finalCart = await this.#addAddonItemsToCart(cartPlan.items);
      this.#triggerAddedState();

      const totalQty = cartPlan.items.reduce(
        (sum, item) => sum + item.quantity,
        0,
      );

      // See the equivalent comment in #addToCartViaApp - must clear this
      // before dispatching CartAddEvent, not in the `finally` block, or other
      // theme components skip re-rendering the drawer's contents.
      delete document.body.dataset.suppressCartDrawer;
      this.#setCartDrawerLoadingOverlay(false);

      this.#productFormComponent?.dispatchEvent(
        new CartAddEvent(
          finalCart,
          String(cartPlan.representativeVariantId),
          {
            source: "product-form-component",
            itemCount: 1,
            productId: this.productId,
            sections: finalCart?.sections,
          },
        ),
      );

      // Trigger cart refresh immediately so data is ready when drawer opens
      document.dispatchEvent(new CustomEvent('theme:cart-refresh'));

      // Mark as successful
      this.#addToCartSuccess = true;
      console.log("[doormat-configurator] Add to cart succeeded (skip base), marked success flag");
      return;
      }

      const [baseItem, ...addonItems] = cartPlan.items;
      if (!baseItem) {
        throw new Error("Unable to build the doormat cart line.");
      }

      const baseCart = await this.#addDoormatToCart(baseItem);
      let finalCart = baseCart;

      if (addonItems.length > 0) {
        try {
          finalCart = await this.#addAddonItemsToCart(addonItems);
        } catch (addonError) {
          await this.#removeCartLineForConfiguration(
            baseCart,
            baseItem,
            cartPlan.configurationId,
          );
          throw addonError;
        }
      }

      this.#triggerAddedState();

      const totalQty = cartPlan.items.reduce(
        (sum, item) => sum + item.quantity,
        0,
      );

      // See the equivalent comment in #addToCartViaApp - must clear this
      // before dispatching CartAddEvent, not in the `finally` block, or other
      // theme components skip re-rendering the drawer's contents.
      delete document.body.dataset.suppressCartDrawer;
      this.#setCartDrawerLoadingOverlay(false);

      this.#productFormComponent?.dispatchEvent(
        new CartAddEvent(finalCart, String(baseItem.id), {
          source: "product-form-component",
          itemCount: 1,
          productId: this.productId,
          sections: finalCart?.sections,
        }),
      );

      // Trigger cart refresh immediately so data is ready when drawer opens
      document.dispatchEvent(new CustomEvent('theme:cart-refresh'));

      // Mark as successful
      this.#addToCartSuccess = true;
      console.log("[doormat-configurator] Add to cart succeeded (addons), marked success flag");
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Something went wrong while adding to cart. Please try again.";
      console.error("[doormat-configurator] add to cart failed", error);
      if (!/sold out|uitverkocht|ausverkauft|vergriffen|épuisé|indisponible/i.test(message)) {
        this.showGlobalError(message);
        this.#scrollIntoView();
      }
      this.#addToCartSuccess = false;
      this.#setCartDrawerLoadingOverlay(false);
      // Nothing actually made it into the cart - close the drawer we opened
      // early rather than leaving it open with stale/loading content.
      const cartDrawer = /** @type {{ closeDialog?: () => void }} */ (
        document.querySelector("cart-drawer-component")
      );
      cartDrawer?.closeDialog?.();
    } finally {
      this.#isAddingToCart = false;
      this.#setAddToCartLoading(false);
      // Re-enable cart drawer
      delete document.body.dataset.suppressCartDrawer;
      console.log("[doormat-configurator] Removed suppression flag (addons), success flag:", this.#addToCartSuccess);

      // Only open the cart drawer if add was successful
      if (this.#addToCartSuccess) {
        // Wait a moment for cart data to propagate, then open drawer
        setTimeout(() => {
          const cartDrawer = document.querySelector("cart-drawer-component");
          if (cartDrawer && typeof cartDrawer.open === "function") {
            console.log("[doormat-configurator] Opening cart drawer after successful add");
            cartDrawer.open();
          } else {
            console.warn("[doormat-configurator] Cart drawer not found or has no open method", { cartDrawer });
          }
        }, 150); // Wait for cart data to settle before opening drawer
      }
      this.#addToCartSuccess = false; // Reset flag
    }
  }

  /**
   * Standard add-to-cart path: no app pricing, no addon pricing.
   * Builds line item properties and adds the resolved variant directly via
   * AJAX - the same pattern used by #addToCartViaApp and #addToCartWithAddons,
   * replacing the old requestSubmit() delegation to product-form-component
   * (which no longer works now that handleSubmit calls event.preventDefault()
   * for all made-to-order products).
   */
  async #addToCartStandard() {
    const variantId = this.#resolveVariantId();
    if (!variantId) {
      this.showGlobalError(
        "This product is not set up for made-to-order checkout yet.",
      );
      this.#scrollIntoView();
      return;
    }

    this.#isAddingToCart = true;
    this.#setAddToCartLoading(true);
    this.hideGlobalError();

    try {
      const configurationId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      const properties = this.#buildLineItemProperties(configurationId);
      await this.#attachPreviewToProperties(properties, configurationId);
      properties["_Calculated price"] = this.formatMoney(
        this.#getTargetPriceCents() / 100,
      );

      const item = {
        id: String(variantId),
        quantity: 1,
        properties,
      };

      const finalCart = await this.#addDoormatToCart(item);
      this.#triggerAddedState();

      this.#productFormComponent?.dispatchEvent(
        new CartAddEvent(finalCart, String(variantId), {
          source: "product-form-component",
          itemCount: 1,
          productId: this.productId,
          sections: finalCart?.sections,
        }),
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Something went wrong while adding to cart. Please try again.";
      console.error("[doormat-configurator] standard add to cart failed", error);
      this.showGlobalError(message);
      this.#scrollIntoView();
    } finally {
      this.#isAddingToCart = false;
      this.#setAddToCartLoading(false);
      this.#enableAddToCartButtons();
    }
  }

  /** @param {CartLineItem} item @param {boolean} isNewVariant @param {boolean} [preventRetry] */
  async #addDoormatToCart(item, isNewVariant = false, preventRetry = false) {
    const cartAddUrl = Theme.routes.cart_add_url || "/cart/add.js";
    // Use more retries for newly created variants that need storefront propagation
    const maxAttempts = isNewVariant ? 7 : 3;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const isLastAttempt = attempt === maxAttempts - 1;

      const formData = new FormData();
      formData.append("id", String(item.id));
      formData.append("quantity", String(item.quantity));

      Object.entries(item.properties || {}).forEach(([key, value]) => {
        formData.append(`properties[${key}]`, String(value));
      });

      const sectionIds = this.#getCartSectionIds();
      if (sectionIds) {
        formData.append("sections", sectionIds);
      }

      /** @type {Response} */
      let response;
      try {
        response = await fetch(
          cartAddUrl,
          fetchConfig("javascript", { body: formData }),
        );
      } catch (networkError) {
        if (isLastAttempt) throw networkError;
        await this.#delay(this.#backoffDelay(attempt, isNewVariant));
        continue;
      }

      // Shopify rate limit: back off (honoring Retry-After) instead of hammering the endpoint.
      if (response.status === 429) {
        if (isLastAttempt) {
          throw new Error(
            "The store is busy right now. Please wait a few seconds and try again.",
          );
        }
        const retryAfter = Number(response.headers.get("Retry-After"));
        const waitMs =
          Number.isFinite(retryAfter) && retryAfter > 0
            ? retryAfter * 1000
            : this.#backoffDelay(attempt, isNewVariant);
        await this.#delay(waitMs);
        continue;
      }

      try {
        return await this.#parseCartResponse(response, "doormat");
      } catch (error) {
        const message = error instanceof Error ? error.message : "";
        const isPropagationLag = /sold out|uitverkocht|unavailable|ausverkauft|vergriffen|épuisé|indisponible|rupture de stock/i.test(message);

        // For new variants, always retry on "sold out" errors (it's just propagation lag)
        if (isPropagationLag && isNewVariant && !isLastAttempt) {
          console.warn(
            `[doormat-configurator] New variant not yet available on storefront, retrying... (attempt ${attempt + 1}/${maxAttempts})`
          );
          await this.#delay(this.#backoffDelay(attempt, isNewVariant));
          continue;
        }

        // For existing variants that are sold out (inventory exhausted), signal the
        // caller to re-resolve a fresh variant from the proxy — unless this IS
        // already a retry attempt, in which case give up with a clear message.
        if (isPropagationLag && !isNewVariant) {
          if (preventRetry) {
            throw new Error(
              "This size is temporarily unavailable. Please try again in a few minutes."
            );
          }
          throw new Error("__SOLD_OUT_EXISTING__");
        }

        if (!isPropagationLag || isLastAttempt) {
          throw error;
        }

        await this.#delay(this.#backoffDelay(attempt, isNewVariant));
      }
    }

    throw new Error("Unable to add the doormat to cart.");
  }

  /**
   * Exponential backoff.
   * New variants: 300ms, 600ms, 1200ms, 2400ms, 5000ms
   *   (fast early retries — if the propagation wait above was not enough,
   *    we want to detect availability quickly rather than waiting 800ms+)
   * Existing variants: 1200ms, 2400ms, 4800ms
   * @param {number} attempt
   * @param {boolean} isNewVariant
   */
  #backoffDelay(attempt, isNewVariant = false) {
    const baseDelay = isNewVariant ? 300 : 1200;
    const maxDelay = 5000;
    return Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
  }

  /** @param {number} ms */
  #delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /** @param {CartLineItem[]} items */
  async #addAddonItemsToCart(items) {
    const cartAddUrl = Theme.routes.cart_add_url || "/cart/add.js";
    const response = await fetch(cartAddUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({
        items: items.map((item) => ({
          id: String(item.id),
          quantity: item.quantity,
          properties: item.properties || {},
        })),
      }),
    });

    return this.#parseCartResponse(response, "sizing add-on");
  }

  /** @param {Response} response @param {string} label */
  async #parseCartResponse(response, label) {
    const responseText = await response.text();
    let data;

    try {
      data = JSON.parse(responseText);
    } catch {
      throw new Error(
        response.ok
          ? `Unexpected response while adding the ${label} to cart.`
          : `Cart error (${response.status}) while adding the ${label}.`,
      );
    }

    if (!response.ok || data.status) {
      throw new Error(this.#formatShopifyCartError(data, label));
    }

    return data;
  }

  /** @param {Record<string, unknown>} data @param {string} label */
  #formatShopifyCartError(data, label) {
    const parts = [];

    if (data?.description) parts.push(data.description);
    else if (data?.message) parts.push(data.message);

    if (data?.errors) {
      if (typeof data.errors === "string") {
        parts.push(data.errors);
      } else {
        Object.entries(data.errors).forEach(([key, value]) => {
          parts.push(`${key}: ${value}`);
        });
      }
    }

    if (parts.length > 0) {
      return parts.join(" ");
    }

    return `Unable to add the ${label} to cart.`;
  }

  #getCartSectionIds() {
    /** @type {string[]} */
    const sectionIds = [];
    document.querySelectorAll("cart-items-component").forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    return sectionIds.join(",");
  }

  /** @param {Record<string, unknown>} cart @param {CartLineItem} baseItem @param {string} configurationId */
  async #removeCartLineForConfiguration(cart, baseItem, configurationId) {
    const items =
      /** @type {Array<{ key?: string; variant_id?: number|string; properties?: Record<string, string> }>} */ (
        cart.items ?? []
      );
    const line = items.find((item) => {
      const matchesVariant = String(item.variant_id) === String(baseItem.id);
      const matchesConfig = item.properties?._Configuration === configurationId;
      return matchesVariant && matchesConfig;
    });

    if (!line?.key) return;

    await fetch(Theme.routes.cart_change_url || "/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ id: line.key, quantity: 0 }),
    });
  }

  #validatePricingConfig() {
    if (!this.pricingConfig.enableAddonPricing) return null;

    if (!this.pricingConfig.addonVariantId) {
      return 'The sizing add-on product is not connected in the theme. In the theme editor, open this product page → Doormat configurator block → Checkout pricing → select "Made-to-order sizing fee". Or set it once under Theme settings → Made-to-order doormats.';
    }

    if (
      !this.pricingConfig.addonUnitPrice ||
      this.pricingConfig.addonUnitPrice <= 0
    ) {
      return "The sizing add-on product must have a price greater than zero (for example €0.50).";
    }

    return null;
  }

  async #buildCartPlan() {
    const variantId = this.#resolveVariantId();
    if (!variantId) {
      this.showGlobalError(
        "This product is not set up for made-to-order checkout yet.",
      );
      return null;
    }

    const configurationId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const properties = this.#buildLineItemProperties(configurationId);
    await this.#attachPreviewToProperties(properties, configurationId);
    const targetPriceCents = this.#getTargetPriceCents();
    properties["_Calculated price"] = this.formatMoney(targetPriceCents / 100);

    const baseVariant = this.variants.find(
      (variant) => String(variant.id) === String(variantId),
    );
    const basePriceCents = baseVariant?.price || 0;
    const remainingCents = Math.max(0, targetPriceCents - basePriceCents);
    const addonUnitPrice = this.pricingConfig.addonUnitPrice || 1;
    const addonQty = Math.ceil(remainingCents / addonUnitPrice);
    const skipBaseItem =
      this.pricingConfig.enableAddonPricing &&
      !this.#isBaseVariantPurchasable(variantId);

    if (skipBaseItem) {
      if (addonQty <= 0) {
        this.showGlobalError(
          "Unable to calculate checkout price for this configuration.",
        );
        return null;
      }

      const inventoryError = this.#validateAddonInventory(addonQty);
      if (inventoryError) {
        this.showGlobalError(inventoryError);
        return null;
      }

      return {
        items: [
          {
            id: String(this.pricingConfig.addonVariantId),
            quantity: addonQty,
            properties: {
              "Size preset": baseVariant?.title || "",
              Size: `${this.currentWidth} x ${this.currentLength} cm`,
              ...properties,
            },
          },
        ],
        configurationId,
        targetPriceCents,
        addonQty,
        skipBaseItem: true,
        representativeVariantId: String(variantId),
      };
    }

    const items = [
      {
        id: String(variantId),
        quantity: 1,
        properties: { ...properties },
      },
    ];

    if (addonQty > 0) {
      const inventoryError = this.#validateAddonInventory(addonQty);
      if (inventoryError) {
        this.showGlobalError(inventoryError);
        return null;
      }

      items.push({
        id: String(this.pricingConfig.addonVariantId),
        quantity: addonQty,
        properties: {
          ...this.#getSourceProductProperties(),
          _Configuration: configurationId,
          Size: `${this.currentWidth} x ${this.currentLength} cm`,
        },
      });
    }

    return {
      items,
      configurationId,
      targetPriceCents,
      addonQty,
      skipBaseItem: false,
      representativeVariantId: String(variantId),
    };
  }

  /** @param {string|number} variantId */
  #isBaseVariantPurchasable(variantId) {
    const variant = this.variants.find(
      (entry) => String(entry.id) === String(variantId),
    );
    return variant?.available !== false;
  }

  /** @param {number} addonQty */
  #validateAddonInventory(addonQty) {
    const {
      addonInventoryTracked,
      addonInventoryPolicy,
      addonInventoryQuantity,
      addonProductTitle,
    } = this.pricingConfig;
    const productName = addonProductTitle || "Made-to-order sizing fee";

    if (
      addonInventoryTracked !== "shopify" ||
      addonInventoryPolicy !== "deny"
    ) {
      return null;
    }

    if (
      addonInventoryQuantity === null ||
      addonInventoryQuantity === undefined
    ) {
      return null;
    }

    if (addonQty <= addonInventoryQuantity) {
      return null;
    }

    return `This configuration needs ${addonQty.toLocaleString()} sizing units at ${this.#getAddonUnitPriceLabel()} each, but "${productName}" only has ${addonInventoryQuantity} in stock. Enable "Continue selling when out of stock" or turn off inventory tracking on that product.`;
  }

  #getAddonUnitPriceLabel() {
    const cents = this.pricingConfig.addonUnitPrice || 1;
    return this.formatMoney(cents / 100);
  }

  /** @returns {Record<string, string>} */
  #getSourceProductProperties() {
    /** @type {Record<string, string>} */
    const properties = {
      Product: this.cartPropProduct,
      "_Doormat product": this.productTitle,
      "_Source product ID": this.productId,
    };

    if (this.productHandle) {
      properties["_Source product handle"] = this.productHandle;
    }

    if (this.productUrl) {
      properties["_Source product URL"] = this.productUrl;
    }

    return properties;
  }

  /**
   * Fires a "Started Configurator" event to Klaviyo the first time the
   * shopper meaningfully interacts with the configurator (size preset,
   * width/length typed, or placement chosen) - once per page load. Used to
   * build an abandoned-configurator flow, similar to abandoned cart.
   *
   * Uses our own ImageURL (the static product image from this.productImage,
   * set from data-product-image on connectedCallback) rather than relying on
   * Klaviyo's automatic image detection - the ephemeral configured-variant
   * preview image doesn't exist yet at this point, and is cleaned up a
   * couple hours after checkout abandonment regardless (see
   * variant-manager.server.js STALE_AFTER_HOURS), so it isn't a safe thing
   * to point Klaviyo at even later on.
   *
   * IMPORTANT: pushes to the window._learnq queue instead of calling
   * window.klaviyo.track() directly. Klaviyo's onsite script is loaded
   * asynchronously by the Klaviyo app embed, and on a fresh/cold page load
   * (first visit, incognito, slow connection) it can still be loading when
   * the shopper's first interaction fires. window.klaviyo.track() would
   * throw/no-op if the script isn't ready yet, and since this method is a
   * one-shot latch (#configuratorStartedTracked), that would silently drop
   * the event for the entire page view with no retry. _learnq is a plain
   * array Klaviyo guarantees to drain once it finishes loading, so pushing
   * to it works correctly regardless of load timing - the same pattern
   * Klaviyo's own docs recommend for firing events before onsite.js is
   * confirmed ready.
   */
  #trackConfiguratorStarted() {
    if (this.#configuratorStartedTracked) return;
    this.#configuratorStartedTracked = true;

    // Shopify renders image URLs as protocol-relative ("//cdn.shopify..."),
    // which works fine in <img src> but Klaviyo's email templates need an
    // explicit scheme to render the image at all.
    const imageUrl = this.productImage.startsWith("//")
      ? `https:${this.productImage}`
      : this.productImage;

    /** @type {Record<string, string>} */
    const properties = {
      Product: this.productTitle,
      ProductID: this.productId,
      ImageURL: imageUrl,
    };
    if (this.productUrl) properties.ProductURL = this.productUrl;
    if (this.productHandle) properties.ProductHandle = this.productHandle;

    try {
      const w = /** @type {any} */ (window);
      w._learnq = w._learnq || [];
      w._learnq.push(["track", "Started Configurator", properties]);
    } catch (_err) {
      // Non-critical - never block the configurator on a tracking failure.
    }
  }

  /** @param {string} configurationId @returns {Record<string, string>} */
  #buildLineItemProperties(configurationId) {
    const width = this.currentWidth ?? 0;
    const length = this.currentLength ?? 0;
    const finalWidth =
      this.currentPlacement === "recessed" ? width + this.margin * 2 : width;
    const finalLength =
      this.currentPlacement === "recessed" ? length + this.margin * 2 : length;

    return {
      ...this.#getSourceProductProperties(),
      [this.cartPropWidth]: `${width} cm`,
      [this.cartPropLength]: `${length} cm`,
      [this.cartPropPlacement]:
        this.currentPlacement === "recessed"
          ? this.labelPlacementRecessed
          : this.labelPlacementFloor,
      [this.cartPropFinalSize]: `${finalWidth} x ${finalLength} cm`,
      _Configuration: configurationId,
      _product_title: this.productTitle,
      _product_handle: this.productHandle,
      _product_url: this.productUrl,
      _configured_product_id: this.productId,
    };
  }

  /**
   * Captures the canvas preview and uploads it to the CDN in the background
   * after a successful cart add. Passes variantId so the server can assign
   * the uploaded image to the Shopify variant for future cart renders.
   * @param {string} variantId
   * @param {string} configurationId
   */
  #uploadPreviewInBackground(variantId, configurationId) {
    setTimeout(async () => {
      try {
        // Reuse early upload if it ran at prefetch time — avoids a duplicate
        // GenericFile in Shopify Files.
        if (this.#earlyPreviewUpload) {
          const earlyUrl = await this.#earlyPreviewUpload.catch(() => null);
          this.#earlyPreviewUpload = null;
          this.#earlyPreviewConfigId = null;
          if (earlyUrl) {
            console.log("[doormat-configurator] Reusing early preview, skipping duplicate upload");
            return;
          }
          // Early upload failed — fall through to new upload below.
        }

        await new Promise((resolve) => setTimeout(resolve, 150));
        const dataUrl = this.#capturePreviewDataUrl();
        if (!dataUrl) return;
        try {
          sessionStorage.setItem(`doormat-preview:${configurationId}`, dataUrl);
        } catch {}
        await this.#uploadPreviewImage(dataUrl, configurationId, variantId);
      } catch {
        // Non-critical — cart item already has _product_image.
      }
    }, 0);
  }

  /** @param {Record<string, string>} properties @param {string} configurationId @param {string} [variantId] */
  async #attachPreviewToProperties(properties, configurationId, variantId) {
    // If prefetch already uploaded the preview for this variant, use that URL
    // directly — skip the base64 step and avoid a duplicate upload request.
    if (variantId && this.#earlyPreviewUpload) {
      const earlyUrl = await this.#earlyPreviewUpload.catch(() => null);
      this.#earlyPreviewUpload = null;
      this.#earlyPreviewConfigId = null;
      if (typeof earlyUrl === 'string') {
        console.log("[doormat-configurator] Using early preview URL", earlyUrl);
        properties._preview_image = earlyUrl;
        return;
      }
    }

    const dataUrl = this.#capturePreviewDataUrl();
    if (!dataUrl) return;

    try {
      sessionStorage.setItem(`doormat-preview:${configurationId}`, dataUrl);
    } catch {
      // Ignore storage quota errors.
    }

    // IMPORTANT: never store raw base64 in cart line item properties — Shopify
    // renders all property values verbatim in the order admin, turning a
    // hundreds-of-KB data URL into an unreadable wall of text in every order.
    // Always wait for the CDN upload and only store the resulting URL.
    // If the upload fails or returns no URL, omit _preview_image entirely;
    // _product_image (set separately) already provides a usable fallback.
    const url = await this.#uploadPreviewImage(dataUrl, configurationId, variantId).catch(() => null);
    if (typeof url === 'string') {
      properties._preview_image = url;
    }
  }

  #capturePreviewDataUrl() {
    if (
      !this.viewerCanvas ||
      this.viewerCanvas.width === 0 ||
      this.viewerCanvas.height === 0
    ) {
      return null;
    }

    try {
      return this.viewerCanvas.toDataURL("image/png");
    } catch {
      return null;
    }
  }

  /** @param {string} dataUrl @param {string} configurationId */
  async #uploadPreviewImage(dataUrl, configurationId, variantId) {
    try {
      const response = await this.#postAppProxy("/preview", {
        productId: this.productId,
        configurationId,
        image: dataUrl,
        variantId,
      }, true); // Use POST for large image data

      if (!response.ok) return null;

      const data = await response.json();
      return typeof data.url === "string" ? data.url : null;
    } catch {
      return null;
    }
  }

  #getTargetPriceCents() {
    if (!this.currentWidth || !this.currentLength) return 0;
    // Recessed installation: price is based on the manufacturing (larger) dimensions.
    const w = this.currentPlacement === "recessed"
      ? this.currentWidth + this.margin * 2
      : this.currentWidth;
    const l = this.currentPlacement === "recessed"
      ? this.currentLength + this.margin * 2
      : this.currentLength;
    const sqm = (w * l) / 10000;
    // Round to the nearest whole euro, matching the server-side calculatePrice().
    return Math.round(sqm * this.pricePerSqm) * 100 + Math.round(this.setupFee) * 100;
  }

  /** "From €X" price based on the minimum custom size (both dimensions at minSize). */
  #getFromPriceCents() {
    const sqm = (this.minSize * this.minSize) / 10000;
    return Math.round(sqm * this.pricePerSqm) * 100 + Math.round(this.setupFee) * 100;
  }

  /** @param {boolean} isLoading */
  #setAddToCartLoading(isLoading) {
    const buttons = document.querySelectorAll(
      `product-form-component[data-product-id="${this.productId}"] [ref="addToCartButton"]`,
    );

    buttons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.disabled = isLoading;
      button.setAttribute("aria-busy", isLoading ? "true" : "false");
    });
  }

  /**
   * Opens the cart drawer immediately when the doormat add-to-cart flow
   * starts, instead of waiting until the preview upload + ephemeral variant
   * creation + propagation retries all finish (which can take several
   * seconds), and puts the drawer's checkout button into a loading state
   * (see #setCartDrawerLoadingOverlay). Calls showDialog() directly (the
   * shared DialogComponent base method) rather than cart-drawer.js's own
   * open(), which early-returns while document.body.dataset.suppressCartDrawer
   * is "true" - we WANT it to open now, even though the real content hasn't
   * arrived yet (that gets morphed in normally once the add actually
   * succeeds - see the suppression-clearing comment above).
   *
   * Note: snippets/header-actions.liquid doesn't render #checkout at all
   * when the cart is empty (cart.empty? swaps in a separate "empty cart"
   * template) - so on a shopper's very first cart action there's briefly no
   * button to show loading on. The drawer still opens either way.
   */
  #openCartDrawerLoading() {
    const cartDrawer = /** @type {{ showDialog?: () => void }} */ (
      document.querySelector("cart-drawer-component")
    );
    if (cartDrawer && typeof cartDrawer.showDialog === "function") {
      cartDrawer.showDialog();
    }
    this.#setCartDrawerLoadingOverlay(true);
    // The loading card (injected by #setCartDrawerLoadingOverlay) covers the
    // entire inner area and includes a product row, so the separate optimistic
    // table row is no longer needed — both empty and non-empty carts now get
    // the same unified card experience.
  }

  /**
   * Injects an optimistic cart row into the existing cart items table so the
   * shopper sees the item immediately, using the same classes and DOM structure
   * as the real server-rendered cart rows. Only runs when the cart is non-empty
   * (i.e. the table already exists in the drawer). Removed as soon as the real
   * cart content morphs in via CartAddEvent / theme:cart-refresh.
   */
  #showOptimisticCartPreview() {
    if (!this.currentWidth || !this.currentLength) return;
    this.#removeOptimisticCartPreview();

    const price = this.#getTargetPriceCents() / 100;
    const priceText = this.formatMoney(price);
    const imageUrl = this.productImage || "";
    const title = this.productTitle || "Custom Doormat";
    const productUrl =
      this.productUrl ||
      (this.productHandle ? `/products/${this.productHandle}` : "#");
    const placement =
      this.currentPlacement === "recessed"
        ? this.labelPlacementRecessed
        : this.labelPlacementFloor;

    // Try multiple selectors to find an existing cart table tbody.
  let tbody =
    document.querySelector("cart-drawer-component .cart-items__table tbody") ||
    document.querySelector(".cart-drawer__items .cart-items__table tbody") ||
    document.querySelector("cart-items-component .cart-items__table tbody");

  if (!tbody) return; // Cart is empty — loading overlay handles it.
    // Build the row with the exact same classes as real cart rows.
    const tr = document.createElement("tr");
    tr.id = "doormat-optimistic-row";
    tr.setAttribute("role", "row");
    tr.className =
      "cart-items__table-row cart-items__table-row--full-width-variants";
    tr.setAttribute("aria-hidden", "true");
    tr.style.opacity = "0.6";

    // --- Media cell ---
    const tdMedia = document.createElement("td");
    tdMedia.className = "cart-items__media";
    tdMedia.setAttribute("role", "cell");
    tdMedia.setAttribute("headers", "productImage");
    if (imageUrl) {
      const a = document.createElement("a");
      a.href = productUrl;
      a.className = "cart-items__media-container";
      a.style.setProperty("--ratio", "1");
      const img = document.createElement("img");
      img.className = "cart-items__media-image border-style";
      img.src = imageUrl;
      img.alt = "";
      img.loading = "eager";
      a.appendChild(img);
      tdMedia.appendChild(a);
    }
    tr.appendChild(tdMedia);

    // --- Details cell ---
    const tdDetails = document.createElement("td");
    tdDetails.className = "cart-items__details cart-primary-typography";
    tdDetails.setAttribute("role", "cell");
    tdDetails.setAttribute("headers", "productInformation");

    // Title
    const infoDiv = document.createElement("div");
    infoDiv.className = "cart-items__product-info";
    const titleP = document.createElement("p");
    const titleA = document.createElement("a");
    titleA.href = productUrl;
    titleA.className = "cart-items__title";
    titleA.textContent = title;
    titleP.appendChild(titleA);
    infoDiv.appendChild(titleP);
    tdDetails.appendChild(infoDiv);

    // Variant subtitle: "Custom {width}x{length}cm" — mirrors the
    // cart-items__variants dl shown by the real server-rendered row.
    const variantsDl = document.createElement("dl");
    variantsDl.className = "cart-items__variants";
    const variantDiv = document.createElement("div");
    variantDiv.className = "cart-items__variant";
    const variantDt = document.createElement("dt");
    variantDt.className = "visually-hidden";
    variantDt.textContent = "Size:";
    const variantDd = document.createElement("dd");
    variantDd.textContent = `Custom ${this.currentWidth}x${this.currentLength}cm`;
    variantDiv.appendChild(variantDt);
    variantDiv.appendChild(variantDd);
    variantsDl.appendChild(variantDiv);
    tdDetails.appendChild(variantsDl);

    // Properties
    const variantsDiv = document.createElement("div");
    variantsDiv.className = "cart-items__variants-wrapper";
    const dl = document.createElement("dl");
    dl.className = "cart-items__properties";

    /** @param {string} label @param {string} value */
    const addProp = (label, value) => {
      const propDiv = document.createElement("div");
      propDiv.className = "cart-items__property";
      const dt = document.createElement("dt");
      dt.textContent = `${label}:`;
      const dd = document.createElement("dd");
      dd.textContent = value;
      propDiv.appendChild(dt);
      propDiv.appendChild(dd);
      dl.appendChild(propDiv);
    };

    addProp("Product", this.productTitle);
    addProp("Horizontal (Width)", `${this.currentWidth} cm`);
    addProp("Vertical (Length)", `${this.currentLength} cm`);
    addProp("Placement", placement);

    const finalW = this.currentPlacement === "recessed"
      ? this.currentWidth + this.margin * 2
      : this.currentWidth;
    const finalL = this.currentPlacement === "recessed"
      ? this.currentLength + this.margin * 2
      : this.currentLength;
    addProp("Final size", `${finalW} x ${finalL} cm`);

    variantsDiv.appendChild(dl);
    tdDetails.appendChild(variantsDiv);

    // Unit price
    const priceDiv = document.createElement("div");
    priceDiv.className = "cart-items__unit-price-wrapper";
    const priceInner = document.createElement("div");
    const priceSpan = document.createElement("span");
    priceSpan.textContent = priceText;
    priceInner.appendChild(priceSpan);
    priceDiv.appendChild(priceInner);
    tdDetails.appendChild(priceDiv);
    tr.appendChild(tdDetails);

    // --- Quantity cell (no controls — item not yet confirmed) ---
    const tdQty = document.createElement("td");
    tdQty.className = "cart-items__quantity";
    tdQty.setAttribute("role", "cell");
    tdQty.setAttribute("headers", "quantity");
    const qtyDiv = document.createElement("div");
    qtyDiv.className = "cart-items__quantity-controls";
    tdQty.appendChild(qtyDiv);
    tr.appendChild(tdQty);

    // --- Error cell ---
    const tdErr = document.createElement("td");
    tdErr.className = "cart-items__error hidden";
    tdErr.setAttribute("role", "cell");
    tdErr.setAttribute("headers", "quantity");
    tr.appendChild(tdErr);

    // --- Line total cell ---
    const tdTotal = document.createElement("td");
    tdTotal.className = "cart-items__price cart-secondary-typography";
    tdTotal.setAttribute("role", "cell");
    tdTotal.setAttribute("headers", "productTotal");
    tdTotal.textContent = priceText;
    tr.appendChild(tdTotal);

    tbody.prepend(tr);
  }

  #removeOptimisticCartPreview() {
    document.getElementById("doormat-optimistic-row")?.remove();
    document.getElementById("doormat-optimistic-wrapper")?.remove();
  }

  /**
   * Injects a minimal spinner style for the checkout button once. The theme
   * has no existing spinner/loading-visual convention (just disabled +
   * aria-busy on plain buttons) - added lazily here rather than in a theme
   * CSS file so this stays self-contained.
   */
  #ensureCartDrawerLoadingStyles() {
    if (document.getElementById("doormat-cart-loading-styles")) return;
    const style = document.createElement("style");
    style.id = "doormat-cart-loading-styles";
    style.textContent = `
      /* Checkout button spinner */
      #checkout.doormat-checkout-loading {
        position: relative;
        color: transparent !important;
        pointer-events: none;
      }
      #checkout.doormat-checkout-loading::after {
        content: "";
        position: absolute;
        top: 50%; left: 50%;
        width: 18px; height: 18px;
        margin: -9px 0 0 -9px;
        border: 2px solid #ffffff;
        border-right-color: transparent;
        border-radius: 50%;
        animation: doormat-checkout-spin 0.6s linear infinite;
      }
      cart-drawer-component .cart-drawer__inner { position: relative; }

      /* ── Unified loading card overlay ─────────────────────────────── */
      .doormat-loading-card {
        position: absolute;
        inset: 0;
        background: var(--color-background, #fff);
        z-index: 1000;
        pointer-events: all;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 2rem;
        gap: 0.875rem;
        overflow-y: auto;
      }
      /* Product row */
      .doormat-loading-card__product-row {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }
      .doormat-loading-card__thumb {
        width: 48px;
        height: 48px;
        border-radius: 8px;
        object-fit: cover;
        flex-shrink: 0;
        background: rgba(0,0,0,0.06);
      }
      .doormat-loading-card__product-text {
        flex: 1;
        min-width: 0;
      }
      .doormat-loading-card__product-title {
        font-size: 0.875rem;
        font-weight: 600;
        margin: 0;
        line-height: 1.3;
        color: inherit;
      }
      .doormat-loading-card__product-subtitle {
        font-size: 0.75rem;
        opacity: 0.5;
        margin: 0.2rem 0 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: inherit;
      }
      /* Progress row: headline + percentage */
      .doormat-loading-card__progress-row {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 0.5rem;
      }
      .doormat-loading-card__headline {
        font-size: 0.875rem;
        font-weight: 600;
        margin: 0;
        line-height: 1.3;
        color: inherit;
      }
      .doormat-loading-card__pct {
        font-size: 0.875rem;
        font-weight: 600;
        flex-shrink: 0;
        color: inherit;
        opacity: 0.6;
      }
      /* Bar */
      .doormat-loading-card__bar-track {
        width: 100%;
        height: 4px;
        background: rgba(0,0,0,0.1);
        border-radius: 2px;
        overflow: hidden;
      }
      .doormat-loading-card__bar-fill {
        height: 100%;
        background: currentColor;
        border-radius: 2px;
        width: 0%;
        transition: width 0.25s ease-out;
      }
      /* Helper text */
      .doormat-loading-card__helper {
        font-size: 0.75rem;
        opacity: 0.45;
        line-height: 1.55;
        margin: 0;
        color: inherit;
      }
      /* Bottom pill */
      .doormat-loading-card__pill {
        background: rgba(0,0,0,0.06);
        border-radius: 999px;
        padding: 0.6875rem 1.25rem;
        text-align: center;
        font-size: 0.8125rem;
        opacity: 0.55;
        color: inherit;
      }

      @keyframes doormat-checkout-spin {
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Puts the cart drawer's checkout button (#checkout, see
   * snippets/cart-summary.liquid) into a disabled + spinner state while the
   * doormat is still being uploaded/created, so the shopper can't try to
   * check out before the item actually exists in their cart. No-op if the
   * button isn't in the DOM (e.g. cart was empty - see #openCartDrawerLoading).
   * Once the real add-to-cart succeeds, component-cart-items.js morphs this
   * button's markup back in from the server, naturally clearing this state -
   * also cleared explicitly (success and error paths) as a safety net.
   *
   * @param {boolean} isLoading
   */
  #setCartDrawerLoadingOverlay(isLoading) {
    if (isLoading) this.#ensureCartDrawerLoadingStyles();

    // Primary: apply spinner to the checkout button (visible when cart is non-empty)
    const checkoutButton = document.getElementById("checkout");
    if (checkoutButton instanceof HTMLButtonElement) {
      checkoutButton.disabled = isLoading;
      checkoutButton.setAttribute("aria-busy", isLoading ? "true" : "false");
      checkoutButton.classList.toggle("doormat-checkout-loading", isLoading);
    }

    // Show a progress indicator whenever loading starts, regardless of cart state.
    const existingOverlay = document.getElementById("doormat-drawer-loading-overlay");
    if (isLoading && !existingOverlay) {
      // Use requestAnimationFrame so the overlay is injected after showDialog()
      // has opened the dialog (showDialog itself defers to rAF internally).
      // Target .cart-drawer__inner which exists in both empty and non-empty cart.
      const injectOverlay = () => {
        const target =
          document.querySelector("cart-drawer-component .cart-drawer__inner") ||
          document.querySelector("cart-drawer-component dialog");
        if (!target || document.getElementById("doormat-drawer-loading-overlay")) return;

        const w = this.currentWidth;
        const l = this.currentLength;

        /** @param {number} p */
        const getStagedHeadline = (p) => {
          if (p >= 90) return this.loadingMsgDone;
          if (p >= 20 && w && l) return this.loadingMsgCutting
            .replace('{w}', String(w))
            .replace('{l}', String(l));
          return this.loadingMsgStart;
        };

        // ── Outer overlay ──
        const overlay = document.createElement("div");
        overlay.id = "doormat-drawer-loading-overlay";
        overlay.className = "doormat-loading-card";
        overlay.setAttribute("aria-live", "polite");
        overlay.setAttribute("aria-label", "Adding to cart, please wait");

        // Product row
        const productRow = document.createElement("div");
        productRow.className = "doormat-loading-card__product-row";
        if (this.productImage) {
          const img = document.createElement("img");
          img.className = "doormat-loading-card__thumb";
          img.src = this.productImage;
          img.alt = "";
          img.loading = "eager";
          productRow.appendChild(img);
        } else {
          const ph = document.createElement("div");
          ph.className = "doormat-loading-card__thumb";
          productRow.appendChild(ph);
        }
        const productText = document.createElement("div");
        productText.className = "doormat-loading-card__product-text";
        const productTitleEl = document.createElement("p");
        productTitleEl.className = "doormat-loading-card__product-title";
        productTitleEl.textContent = this.loadingProductTitle;
        const productSubtitleEl = document.createElement("p");
        productSubtitleEl.className = "doormat-loading-card__product-subtitle";
        productSubtitleEl.textContent = w && l
          ? `${this.productTitle} · ${w} × ${l} cm`
          : this.productTitle;
        productText.appendChild(productTitleEl);
        productText.appendChild(productSubtitleEl);
        productRow.appendChild(productText);
        overlay.appendChild(productRow);

        // Progress row: headline + percentage
        const progressRow = document.createElement("div");
        progressRow.className = "doormat-loading-card__progress-row";
        const headlineEl = document.createElement("p");
        headlineEl.className = "doormat-loading-card__headline";
        headlineEl.textContent = getStagedHeadline(0);
        const pctEl = document.createElement("span");
        pctEl.className = "doormat-loading-card__pct";
        pctEl.textContent = "0%";
        progressRow.appendChild(headlineEl);
        progressRow.appendChild(pctEl);
        overlay.appendChild(progressRow);

        // Bar
        const barTrack = document.createElement("div");
        barTrack.className = "doormat-loading-card__bar-track";
        const barFill = document.createElement("div");
        barFill.className = "doormat-loading-card__bar-fill";
        barTrack.appendChild(barFill);
        overlay.appendChild(barTrack);

        // Static helper text
        const helperEl = document.createElement("p");
        helperEl.className = "doormat-loading-card__helper";
        helperEl.textContent = this.loadingHelper;
        overlay.appendChild(helperEl);

        // Bottom pill (disabled CTA stand-in)
        const pillEl = document.createElement("div");
        pillEl.className = "doormat-loading-card__pill";
        pillEl.textContent = this.loadingPill;
        overlay.appendChild(pillEl);

        target.appendChild(overlay);

        // Animate: fast start, decelerate toward ceiling (never reaches 100%)
        const CEILING = 97;
        let pct = 0;
        const timerId = setInterval(() => {
          pct = Math.min(pct + (CEILING - pct) * 0.07, CEILING);
          barFill.style.width = `${pct.toFixed(1)}%`;
          pctEl.textContent = `${Math.round(pct)}%`;
          headlineEl.textContent = getStagedHeadline(pct);
        }, 250);
        /** @type {any} */ (overlay)._timer = timerId;
      };
      requestAnimationFrame(injectOverlay);
    } else if (!isLoading && existingOverlay) {
      clearInterval(/** @type {any} */ (existingOverlay)._timer);
      existingOverlay.remove();
    }

    // Remove optimistic row when loading ends (real content will morph in).
    if (!isLoading) {
      this.#removeOptimisticCartPreview();
    }
  }

  #triggerAddedState() {
    document
      .querySelectorAll(
        `product-form-component[data-product-id="${this.productId}"] add-to-cart-component`,
      )
      .forEach((component) => {
        const addToCartComponent =
          /** @type {{ animateAddToCart?: () => void }} */ (component);
        addToCartComponent.animateAddToCart?.();
      });
  }

  #enableAddToCartButtons() {
    document
      .querySelectorAll(
        `product-form-component[data-product-id="${this.productId}"] add-to-cart-component`,
      )
      .forEach((component) => {
        const addToCartComponent = /** @type {{ enable?: () => void }} */ (
          component
        );
        addToCartComponent.enable?.();
      });
  }

  #disableAllATCButtons() {
    // Disable all name="add" buttons
    document.querySelectorAll('[name="add"]').forEach((button) => {
      if (button instanceof HTMLButtonElement) {
        button.disabled = true;
      }
    });

    // Disable all submit buttons in cart add forms
    document.querySelectorAll('button[type="submit"]').forEach((button) => {
      if (
        button instanceof HTMLButtonElement &&
        button.closest('form[action*="/cart/add"]')
      ) {
        button.disabled = true;
      }
    });
  }

  #validate() {
    this.hideGlobalError();
    const errors = [];

    if (!this.currentWidth || !this.currentLength) {
      errors.push("Please enter the dimensions");
    }

    if (!this.currentPlacement) {
      errors.push("Please select a placement option");
      this.placementOptions.forEach((opt) => opt.classList.add("error"));
      if (this.placementError) {
        this.placementError.textContent =
          "Please select how the mat will be placed";
      }
    }

    return errors;
  }

  /** @param {HTMLFormElement} form */
  async #syncFormFields(form) {
    const configurationId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const properties = this.#buildLineItemProperties(configurationId);
    await this.#attachPreviewToProperties(properties, configurationId);
    properties["_Calculated price"] = this.formatMoney(
      this.#getTargetPriceCents() / 100,
    );

    form
      .querySelectorAll('input[name^="properties"]')
      .forEach((input) => input.remove());

    Object.entries(properties).forEach(([key, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = `properties[${key}]`;
      input.value = value;
      form.appendChild(input);
    });

    const variantId = this.#resolveVariantId();
    const variantInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('[name="id"]')
    );
    if (variantInput && variantId) {
      variantInput.value = String(variantId);
      variantInput.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  #resolveVariantId() {
    if (!this.variants.length) return null;

    const fromSizeButton = this.#resolveVariantIdFromActiveSizeButton();
    if (fromSizeButton) return fromSizeButton;

    if (this.pricingConfig.enableAddonPricing) {
      return this.#resolveAddonPricingFallbackVariantId();
    }

    const formVariantId = this.#getFormVariantId();
    if (formVariantId) return formVariantId;

    if (this.checkoutVariantId) return this.checkoutVariantId;

    return this.variants[0]?.id ?? null;
  }

  #resolveVariantIdFromActiveSizeButton() {
    const activeButton = this.#getActiveSizeButton();
    if (!activeButton) return null;

    const buttonVariantId = activeButton.dataset.variantId;
    if (buttonVariantId) return String(buttonVariantId);

    const size = activeButton.dataset.size;
    if (size && size !== "custom") {
      const presetId = this.#resolvePresetVariantId(size);
      if (presetId) return presetId;
    }

    if (size === "custom") {
      const variantTitle = activeButton.dataset.variantTitle;
      if (variantTitle) {
        const byTitle = this.#findVariantByTitle(variantTitle);
        if (byTitle) return String(byTitle.id);
      }

      if (this.checkoutVariantId) return this.checkoutVariantId;
    }

    return null;
  }

  #resolveAddonPricingFallbackVariantId() {
    const freeAvailable = this.variants.find(
      (variant) =>
        variant.available !== false && (variant.price === 0 || !variant.price),
    );
    if (freeAvailable) return String(freeAvailable.id);

    const anyAvailable = this.variants.find(
      (variant) => variant.available !== false,
    );
    if (anyAvailable) return String(anyAvailable.id);

    if (this.checkoutVariantId) return this.checkoutVariantId;

    return this.variants[0] ? String(this.variants[0].id) : null;
  }

  #getFormVariantId() {
    const input = /** @type {HTMLInputElement | null} */ (
      this.#productFormComponent?.querySelector('[name="id"]')
    );
    const value = input?.value?.trim();
    return value || null;
  }

  /** @param {string} presetKey */
  #resolvePresetVariantId(presetKey) {
    const activeButton = this.querySelector(`[data-size="${presetKey}"]`);
    if (activeButton instanceof HTMLElement && activeButton.dataset.variantId) {
      return activeButton.dataset.variantId;
    }

    const variantTitle =
      activeButton instanceof HTMLElement
        ? activeButton.dataset.variantTitle
        : undefined;
    if (variantTitle) {
      const byTitle = this.#findVariantByTitle(variantTitle);
      if (byTitle) return String(byTitle.id);
    }

    /** @type {Record<string, string[]>} */
    const presetMap = {
      "single-door": ["single door", "single", "90 x 60", "90x60"],
      "double-door": [
        "double door",
        "double",
        "180 x 65",
        "180x65",
        "180 x 60",
        "180x60",
      ],
      "90x60": ["single door", "single", "90 x 60", "90x60"],
      "180x65": ["double door", "double", "180 x 65", "180x65"],
      "180x60": ["double door", "double", "180 x 60", "180x60"],
    };

    const needles = presetMap[presetKey] || [];
    const match = this.variants.find((variant) => {
      const title = (variant.title || "").toLowerCase();
      return needles.some((needle) => title.includes(needle));
    });
    return match ? String(match.id) : null;
  }

  /** @param {string} title */
  #findVariantByTitle(title) {
    const needle = title.trim().toLowerCase();
    return this.variants.find((variant) => {
      const variantTitle = (variant.title || "").trim().toLowerCase();
      return variantTitle === needle || variantTitle.includes(needle);
    });
  }

  #scrollIntoView() {
    window.scrollTo({ top: this.offsetTop - 100, behavior: "smooth" });
  }

  #preloadPattern() {
    const patternUrl = this.patternUrl;
    if (!patternUrl) return;
    this.patternImageLoading = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        this.patternImage = img;
        resolve(img);
      };
      img.onerror = () => resolve(null);
      img.src = patternUrl;
    });
  }

  #initializeDefaultState() {
    const defaultButton =
      this.querySelector(`[data-size="${this.defaultSizeKey}"]`) ||
      this.querySelector('[data-size="single-door"]') ||
      this.querySelector('[data-size="custom"]');

    if (defaultButton instanceof HTMLElement) {
      this.handleSizeButtonClick(defaultButton);
    } else {
      this.#activeSizeMode = "custom";
      if (this.widthInput) this.widthInput.value = "";
      if (this.lengthInput) this.lengthInput.value = "";
      this.handleInputChange(true);
    }
  }

  /**
   * Redesign per client request: mirror the studiotapiz.nl pattern - the
   * live pattern preview replaces the main product image area (not a
   * separate panel further down the page), behind a small Photos/Preview
   * tab so the customer can still see the real product photos. Available
   * for every size mode (Custom and the Single/Double Door presets) - it
   * starts on Photos and switches to Preview as soon as the customer
   * configures something (types a dimension, picks a placement, or picks a
   * preset with fixed dimensions).
   *
   * Done entirely from JS (no changes to the shared media-gallery block/
   * snippet, which every other product on the store also uses) - we just
   * locate .product-information__media (this product page's own gallery
   * column) at runtime and dock our existing viewer container into it. If
   * that container isn't found (different template, theme editor, etc.)
   * this is a no-op and the viewer stays inline where the block already
   * renders it - unaffected.
   */
  handleSizeButtonClick(/** @type {HTMLElement} */ button) {
    this.#trackConfiguratorStarted();
    this.sizeButtons.forEach((btn) => btn.classList.remove("active"));
    button.classList.add("active");

    const size = button.dataset.size ?? "custom";
    this.#activeSizeMode = size;

    const presetWidth = button.dataset.presetWidth;
    const presetLength = button.dataset.presetLength;
    const isPreset = Boolean(presetWidth && presetLength);

    // Preset sizes have fixed dimensions - inputs should not be editable.
    if (this.widthInput) this.widthInput.disabled = isPreset;
    if (this.lengthInput) this.lengthInput.disabled = isPreset;

    if (presetWidth && presetLength) {
      if (this.widthInput) this.widthInput.value = presetWidth;
      if (this.lengthInput) this.lengthInput.value = presetLength;
      this.lastVisualWidth = Number(presetWidth);
      this.lastVisualLength = Number(presetLength);
      this.handleInputChange(false);
    } else {
      if (this.widthInput) this.widthInput.value = "";
      if (this.lengthInput) this.lengthInput.value = "";
      this.lastVisualWidth = null;
      this.lastVisualLength = null;
      this.widthInput?.focus();
      this.handleInputChange(true);
    }

    this.#applyVariantSelection({
      variantId: button.dataset.variantId || null,
      variantTitle: button.dataset.variantTitle || null,
    });
  }

  #getActiveSizeButton() {
    const button = this.querySelector("[data-size].active");
    return button instanceof HTMLElement ? button : null;
  }

  handleInputChange(updateVisualReference = true) {
    this.hideGlobalError();

    const width = parseFloat(this.widthInput?.value || "");
    const length = parseFloat(this.lengthInput?.value || "");

    this.widthInput?.classList.remove("error");
    this.lengthInput?.classList.remove("error");
    if (this.widthError) { this.widthError.textContent = ""; this.widthError.style.display = ""; }
    if (this.lengthError) { this.lengthError.textContent = ""; this.lengthError.style.display = ""; }
    if (this.combinedError) { this.combinedError.textContent = ""; this.combinedError.style.display = ""; }
    if (this.placementError) this.placementError.textContent = "";

    let isValid = true;

    /** @param {HTMLElement|null} el @param {string} msg */
    const setFieldError = (el, msg) => {
      if (!el) return;
      el.textContent = msg;
      el.style.display = "block";
    };

    // Individual field: below min or above absolute max (long side limit)
    if (
      this.widthInput?.value &&
      (isNaN(width) || width < this.minSize || width > this.maxSize)
    ) {
      this.widthInput.classList.add("error");
      setFieldError(this.widthError, this.errorMinMax.replace('{min}', String(this.minSize)).replace('{max}', String(this.maxSize)));
      isValid = false;
    }

    if (
      this.lengthInput?.value &&
      (isNaN(length) || length < this.minSize || length > this.maxSize)
    ) {
      this.lengthInput.classList.add("error");
      setFieldError(this.lengthError, this.errorMinMax.replace('{min}', String(this.minSize)).replace('{max}', String(this.maxSize)));
      isValid = false;
    }

    // Combined 504×180 rule: the shorter side may not exceed maxShortSide
    if (isValid && !isNaN(width) && !isNaN(length) && width > 0 && length > 0) {
      const shortSide = Math.min(width, length);
      if (shortSide > this.maxShortSide) {
        this.widthInput?.classList.add("error");
        this.lengthInput?.classList.add("error");
        const msg = this.errorShortSide.replace(/\{max\}/g, String(this.maxShortSide));
        if (this.combinedError) { this.combinedError.textContent = msg; this.combinedError.style.display = "block"; }
        isValid = false;
      }
    }

    // Recessed installation: manufacturing dimensions add margin to both sides —
    // validate that the final (larger) dimensions still fit within the max.
    if (isValid && !isNaN(width) && !isNaN(length) && width > 0 && length > 0 && this.currentPlacement === "recessed") {
      const rW = width + this.margin * 2;
      const rL = length + this.margin * 2;
      const rShort = Math.min(rW, rL);
      const rLong = Math.max(rW, rL);
      if (rShort > this.maxShortSide || rLong > this.maxSize) {
        this.widthInput?.classList.add("error");
        this.lengthInput?.classList.add("error");
        const addedCm = (this.margin * 2).toFixed(1).replace(".0", "");
        const msg = this.errorRecessed.replace('{added}', addedCm);
        if (this.combinedError) { this.combinedError.textContent = msg; this.combinedError.style.display = "block"; }
        isValid = false;
      }
    }

    if (isValid && width && length) {
      this.currentWidth = width;
      this.currentLength = length;

      if (
        updateVisualReference ||
        !this.lastVisualWidth ||
        !this.lastVisualLength
      ) {
        this.lastVisualWidth = width;
        this.lastVisualLength = length;
      }

      this.updatePrice();
      this.updateViewer();
      this.updateSummary();
      // For preset sizes, prefetch immediately when placement is set.
      // Custom mode skips this — prefetch fires on placement selection instead,
      // so no variant is created for an unfinished/abandoned configuration.
      if (this.currentPlacement && this.#activeSizeMode !== 'custom') {
        this.#maybePrefetchVariant();
      }
    } else {
      this.currentWidth = null;
      this.currentLength = null;
      this.hideViewer();
      this.updateSummary();
      this.updatePriceToDefault();
    }

    if (!this.widthInput?.value && !this.lengthInput?.value) {
      this.#activeSizeMode = "custom";
      this.sizeButtons.forEach((btn) => {
        btn.classList.toggle("active", btn.dataset.size === "custom");
      });
    }
  }

  handlePlacementClick(/** @type {HTMLElement} */ option) {
    this.#trackConfiguratorStarted();
    this.hideGlobalError();
    this.placementOptions.forEach((opt) => {
      opt.classList.remove("active", "error");
      const radio = /** @type {HTMLInputElement | null} */ (
        opt.querySelector("[data-placement-radio]")
      );
      if (radio) radio.checked = false;
    });
    option.classList.add("active");
    const radio = /** @type {HTMLInputElement | null} */ (
      option.querySelector("[data-placement-radio]")
    );
    if (radio) radio.checked = true;
    this.currentPlacement = option.dataset.placement ?? null;
    if (this.placementError) this.placementError.textContent = "";
    // Re-run dimension validation so recessed size/price checks update immediately.
    this.handleInputChange(false);
    // All fields may now be complete — start variant prefetch immediately.
    this.#maybePrefetchVariant();
  }

  updatePrice() {
    // Show price as soon as both dimensions are entered - no need to wait for
    // placement, because the price is based purely on entered W×H (placement
    // only affects the final-size margin shown in the summary, not the price).
    if (!this.currentWidth || !this.currentLength) {
      this.updatePriceToDefault();
      return;
    }

    const price = this.#getTargetPriceCents() / 100;
    const formatted = this.formatMoney(price);
    if (this.priceDisplay) {
      this.priceDisplay.textContent = formatted;
    }
    this.#updateStickyPrice(formatted);
  }

  updatePriceToDefault() {
    if (!this.priceDisplay) return;

    let text;
    if (this.#activeSizeMode === "custom") {
      // "From €X" based on the minimum custom size, instead of a blank/
      // premature calculated price.
      const fromPrice = this.#getFromPriceCents() / 100;
      text = `${this.priceFrom} ${this.formatMoney(fromPrice)}`;
    } else {
      text = "";
    }
    this.priceDisplay.textContent = text;
    this.#updateStickyPrice(text);
  }

  /**
   * Mirrors the configurator's calculated price into the sticky add-to-cart
   * bar's price element ([data-doormat-sticky-price]) so the two always stay
   * in sync. The sticky bar renders a plain <span> for made-to-order products
   * instead of the standard price snippet (see product-information.liquid).
   * @param {string} text
   */
  #updateStickyPrice(text) {
    document
      .querySelectorAll(`[data-doormat-sticky-price][data-product-id="${this.productId}"]`)
      .forEach((el) => {
        el.textContent = text;
      });
  }

  formatMoney(/** @type {number} */ value) {
    const moneyFormat = this.moneyFormat;
    const major = Number(value).toFixed(2);
    if (moneyFormat.includes("{{amount_with_comma_separator}}")) {
      return moneyFormat.replace(
        "{{amount_with_comma_separator}}",
        major.replace(".", ","),
      );
    }
    if (moneyFormat.includes("{{amount_no_decimals}}")) {
      return moneyFormat.replace(
        "{{amount_no_decimals}}",
        Math.round(value).toString(),
      );
    }
    if (moneyFormat.includes("{{amount}}")) {
      return moneyFormat.replace("{{amount}}", major);
    }
    return new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  }

  async updateViewer() {
    if (
      !this.viewerCanvas ||
      !this.viewerContainer ||
      !this.viewerPlaceholder ||
      !this.currentWidth ||
      !this.currentLength ||
      !this.lastVisualWidth ||
      !this.lastVisualLength
    ) {
      this.hideViewer();
      return;
    }

    const visualWidth = this.lastVisualWidth;
    const visualLength = this.lastVisualLength;

    const styles = window.getComputedStyle(this.viewerContainer);
    const paddingX =
      parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
    const paddingY =
      parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
    const availableWidth = Math.max(
      this.viewerContainer.clientWidth - paddingX,
      120,
    );
    const availableHeight = Math.max(
      this.viewerContainer.clientHeight - paddingY,
      120,
    );

    const scaleX = visualWidth / this.patternScaleX;
    const scaleY = visualLength / this.patternScaleY;
    const tilesXWhole = Math.abs(scaleX - Math.round(scaleX)) < 0.0001;
    const tilesYWhole = Math.abs(scaleY - Math.round(scaleY)) < 0.0001;

    const pixelsPerCm = Math.min(
      (availableWidth * 0.92) / visualWidth,
      (availableHeight * 0.92) / visualLength,
    );

    let tilePxW = Math.max(4, this.patternScaleX * pixelsPerCm);
    let tilePxH = Math.max(4, this.patternScaleY * pixelsPerCm);

    let displayW = Math.max(40, Math.round(visualWidth * pixelsPerCm));
    let displayH = Math.max(40, Math.round(visualLength * pixelsPerCm));

    if (tilesXWhole) {
      tilePxW = Math.round(tilePxW);
      displayW = Math.round(Math.round(scaleX) * tilePxW);
    }

    if (tilesYWhole) {
      tilePxH = Math.round(tilePxH);
      displayH = Math.round(Math.round(scaleY) * tilePxH);
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const matBg =
      getComputedStyle(this).getPropertyValue("--doormat-mat-bg").trim() ||
      "#ffffff";
    const matBorderColor =
      getComputedStyle(this)
        .getPropertyValue("--doormat-mat-border-color")
        .trim() || "#000000";
    const matBorderWidth =
      parseFloat(
        getComputedStyle(this).getPropertyValue("--doormat-mat-border-width"),
      ) || 0;

    this.viewerPlaceholder.style.display = "none";
    this.viewerCanvas.style.display = "block";
    this.viewerCanvas.width = Math.round(displayW * dpr);
    this.viewerCanvas.height = Math.round(displayH * dpr);
    this.viewerCanvas.style.width = `${displayW}px`;
    this.viewerCanvas.style.height = `${displayH}px`;

    const ctx = this.viewerCanvas.getContext("2d");
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.fillStyle = matBg;
    ctx.fillRect(0, 0, displayW, displayH);

    if (!this.patternUrl) {
      this.#drawMatBorder(
        ctx,
        displayW,
        displayH,
        matBorderWidth,
        matBorderColor,
      );
      return;
    }

    if (!this.patternImage && this.patternImageLoading) {
      await this.patternImageLoading;
    }

    if (!this.patternImage) {
      this.#drawMatBorder(
        ctx,
        displayW,
        displayH,
        matBorderWidth,
        matBorderColor,
      );
      return;
    }

    const img = this.patternImage;
    const tileW = Math.ceil(tilePxW);
    const tileH = Math.ceil(tilePxH);

    let startX = 0;
    let startY = 0;

    if (this.centerPattern) {
      // Place a tile CENTER at the canvas centre.
      // Formula: startX = (displayW/2 - tileW/2) - n*tileW
      // where n = ceil((displayW/2 - tileW/2) / tileW) puts startX in (-tileW, 0].
      //
      // For odd tile counts the formula produces startX=0 which is
      // mathematically correct (the middle tile IS centred) but visually
      // indistinguishable from the non-centred top-left mode.
      // When that happens, apply an extra -tileW/2 shift so the canvas
      // shows symmetric partial tiles on each edge — a visible difference
      // from startX=0 for ALL tile counts (odd and even).
      const nX = Math.ceil((displayW / 2 - tileW / 2) / tileW);
      startX = displayW / 2 - tileW / 2 - nX * tileW;
      if (startX >= 0) startX = -tileW / 2;

      const nY = Math.ceil((displayH / 2 - tileH / 2) / tileH);
      startY = displayH / 2 - tileH / 2 - nY * tileH;
      if (startY >= 0) startY = -tileH / 2;
    }

    const cols = Math.ceil((displayW - startX) / tileW) + 1;
    const rows = Math.ceil((displayH - startY) / tileH) + 1;

    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, displayW, displayH);
    ctx.clip();

    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < cols; col += 1) {
        const x = Math.round(startX + col * tileW);
        const y = Math.round(startY + row * tileH);
        ctx.drawImage(img, x, y, tileW + 1, tileH + 1);
      }
    }

    ctx.restore();
    this.#drawMatBorder(
      ctx,
      displayW,
      displayH,
      matBorderWidth,
      matBorderColor,
    );
  }

  /** @param {CanvasRenderingContext2D} ctx @param {number} width @param {number} height @param {number} borderWidth @param {string} borderColor */
  #drawMatBorder(ctx, width, height, borderWidth, borderColor) {
    if (!borderWidth || borderWidth <= 0) return;

    ctx.save();
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = borderWidth;
    ctx.strokeRect(
      borderWidth / 2,
      borderWidth / 2,
      width - borderWidth,
      height - borderWidth,
    );
    ctx.restore();
  }

  hideViewer() {
    if (this.viewerCanvas) {
      this.viewerCanvas.style.display = "none";
      this.viewerCanvas.width = 0;
      this.viewerCanvas.height = 0;
    }
    if (this.viewerPlaceholder) this.viewerPlaceholder.style.display = "flex";
  }

  updateSummary() {
    if (!this.summarySection) return;

    if (!this.currentWidth || !this.currentLength) {
      this.summarySection.hidden = true;
      return;
    }

    this.summarySection.hidden = false;

    if (this.summarySize && this.currentWidth && this.currentLength) {
      this.summarySize.textContent = `${this.currentWidth} x ${this.currentLength} cm`;
    }

    if (!this.currentPlacement || !this.currentWidth || !this.currentLength) {
      if (this.summaryPlacement) this.summaryPlacement.textContent = "—";
      if (this.summaryFinal) this.summaryFinal.textContent = "—";
      return;
    }

    if (
      this.currentPlacement === "recessed" &&
      this.currentWidth &&
      this.currentLength
    ) {
      const finalWidth = this.currentWidth + this.margin * 2;
      const finalLength = this.currentLength + this.margin * 2;
      if (this.summaryPlacement)
        this.summaryPlacement.textContent = this.labelPlacementRecessed;
      if (this.summaryFinal) {
        this.summaryFinal.textContent = `${finalWidth} x ${finalLength} cm ${this.labelWithMargin}`;
      }
    } else {
      if (this.summaryPlacement)
        this.summaryPlacement.textContent = this.labelPlacementFloor;
      if (this.summaryFinal) {
        this.summaryFinal.textContent = `${this.currentWidth} x ${this.currentLength} cm ${this.labelTolerance}`;
      }
    }
  }

  openModal(/** @type {string | undefined} */ type) {
    if (!type) return;
    const data = this.modalData[type];
    if (!data || !this.modal) return;

    if (this.modalTitle) this.modalTitle.textContent = data.title || "";
    if (this.modalContent) this.modalContent.innerHTML = data.content || "";

    if (data.image && this.modalImage && this.modalImageWrap) {
      this.modalImageWrap.removeAttribute("hidden");
      this.modalImage.src = data.image;
      this.modalImage.alt = data.title || "";
    } else if (this.modalImageWrap) {
      this.modalImageWrap.setAttribute("hidden", "");
      if (this.modalImage) this.modalImage.removeAttribute("src");
    }

    this.modal.classList.add("visible");
    this.modal.setAttribute("aria-hidden", "false");
    document.body.classList.add("doormat-modal-open");
  }

  closeModal() {
    this.modal?.classList.remove("visible");
    this.modal?.setAttribute("aria-hidden", "true");
    document.body.classList.remove("doormat-modal-open");
  }

  showGlobalError(/** @type {string} */ message) {
    if (this.globalErrorText) this.globalErrorText.textContent = message;
    this.globalError?.classList.add("visible");
  }

  hideGlobalError() {
    this.globalError?.classList.remove("visible");
    if (this.globalErrorText) this.globalErrorText.textContent = "";
  }
}

if (!customElements.get("doormat-configurator")) {
  customElements.define("doormat-configurator", DoormatConfigurator);
}
